<template>
    <div>
        <div v-if="property">
            <!-- Top Heading -->
            <b-row>
                <div class="col-12 col-lg-8 mb-5">
                    <h1 class="page-title">
                        {{ isNew ? 'Add' : 'Edit' }} Property
                    </h1>
                    <p>In the below section you can add and amend the text and features within your online property advert. You can make amendments to your online advert 24/7 and these will be automatically sent to our central property team for final review prior to being published to the property portal sites. Please note that we do not publish your house name or number on our website or access our network or partner property websites.</p>
                </div>
            </b-row>
            <!-- END Top Heading -->

            <!-- Package / Actions -->
            <b-row>
                <b-col
                    cols="12"
                    lg="6"
                    class="mb-5"
                >
                    <!-- Package -->
                    <div class="card h-100">
                        <div class="card__body d-flex flex-column">
                            <!-- Package description -->
                            <h2 class="card__title color-dark-grey mb-5">
                                Your current package
                            </h2>
                            <!-- Package Name -->
                            <h3
                                :class="{ ['package-color-' + property.package.color]: property.package.color }"
                                class="d-flex align-items-center mb-5"
                            >
                                <SVGSprite
                                    :name="'package-item-' + property.package.color"
                                    class="package-icon-basket"
                                />
                                <span>{{ property.package.name }}</span>
                            </h3>
                            <!-- END Package Name -->
                            <p>
                                This is the current package that is active for your property.
                                {{ propertyProducts.length ? 'The following products are part of the package or have been purchased separately.' : '' }}
                            </p>
                            <!-- END Package description -->

                            <!-- Package details -->
                            <ul
                                v-if="property.products"
                                class="icon-list icon-list--split list-unstyled mb-0 mt-4"
                            >
                                <li
                                    v-for="product in propertyProducts"
                                    :key="product.id"
                                    class="d-flex align-items-center"
                                >
                                    <SVGSprite
                                        :name="product.icon || 'messages'"
                                        class="icon-list__svg"
                                    />
                                    <span class="p-sm">{{ product.name }}</span>
                                </li>
                            </ul>
                            <!-- END Package details -->

                            <!-- Upgrade package -->
                            <div
                                v-if="!hasAllProducts"
                                class="d-flex justify-content-lg-end mt-auto"
                            >
                                <button
                                    type="button"
                                    class="btn btn--primary"
                                    @click="saveBeforeLeavingRoute = {
                                        name: 'checkout',
                                        params: {
                                            propertyId: property.id
                                        }
                                    }"
                                >
                                    Add extras
                                </button>
                            </div>
                            <!-- END Upgrade package -->
                        </div>
                    </div>
                    <!-- END Package -->
                </b-col>
                <b-col
                    cols="12"
                    lg="6"
                    class="mb-5"
                >
                    <!-- Actions -->
                    <div class="card alerts-card h-100">
                        <div class="card__body d-flex flex-column">
                            <!-- Details table -->
                            <div class="table-flex mb-5">
                                <div v-if="property.status.display_name">
                                    <div>Status</div>
                                    <div class="text-lg-right">
                                        {{ property.status.display_name }}
                                    </div>
                                </div>
                                <div v-if="property.listed_at">
                                    <div>Published</div>
                                    <div class="text-lg-right">
                                        {{ property.listed_at | moment('L') }}
                                    </div>
                                </div>
                                <div v-if="property.expires_at">
                                    <div>Listing Expiry</div>
                                    <div class="text-lg-right">
                                        {{ property.expires_at | moment('L') }}
                                    </div>
                                </div>
                                <div v-if="property.updated_at">
                                    <div>Last Updated</div>
                                    <div class="text-lg-right">
                                        {{ property.updated_at | moment('L') }}
                                    </div>
                                </div>
                            </div>
                            <!-- END Details table -->

                            <div>
                                <p
                                    v-if="property.status.name === 'draft'"
                                    class="text-bold"
                                >
                                    Your property is only viewable by you and the My Online Estate Agent team. This allows
                                    you to complete all the details for your property prior to submitting it for publication.
                                </p>
                                <p
                                    v-else-if="property.status.name === 'under_review'"
                                    class="text-bold"
                                >
                                    Your property is current awaiting review by a member of our team. This process may take
                                    up to 48 hours. Once it has been approved, you will recieve a notification and your
                                    property will be listed in property searches.
                                </p>
                                <p
                                    v-else-if="property.status.name === 'available'"
                                    class="text-bold"
                                >
                                    Your property is now publically listed in property searches.
                                </p>
                                <p
                                    v-else-if="property.status.name === 'expired'"
                                    class="text-bold"
                                >
                                    The listing for your property has expired. If you wish to relist the property, please
                                    contact the My Online Estate Agent team.
                                </p>
                            </div>

                            <!-- Alerts -->

                            <div
                                v-if="notifications.length"
                                class="d-flex flex-column bg-lightest-grey p-4 p-lg-5 mt-4 rounded-corners"
                            >
                                <h4 class="text-bold mb-4">
                                    Notifications
                                </h4>
                                <div class="mb-4">
                                    <ul class="icon-list icon-list--alert list-unstyled mb-0">
                                        <li
                                            v-for="notification in notifications.splice(0, 3)"
                                            :key="notification.id"
                                            class="d-flex"
                                        >
                                            <SVGSprite
                                                name="bell"
                                                class="icon-list__svg"
                                            />
                                            <span class="p-sm color-primary text-bold">{{ notification.content }}</span>
                                        </li>
                                    </ul>
                                </div>
                                <div class="d-flex justify-content-end">
                                    <router-link
                                        :to="{ name: 'notifications' }"
                                        class="btn btn--secondary"
                                    >
                                        View All
                                    </router-link>
                                </div>
                            </div>

                            <!-- END Alerts -->

                            <!-- Property actions -->
                            <div class="row mt-auto">
                                <div class="col-12 col-xl-6 d-flex flex-column">
                                    <div class="mt-auto">
                                        <div
                                            v-if="property.status.name === 'available'"
                                            class="mb-3"
                                        >
                                            <button
                                                class="color-mid-grey text-underline px-0"
                                                type="button"
                                                @click="$refs.modalUnlistProperty.show()"
                                            >
                                                Unlist Property
                                            </button>
                                        </div>
                                        <div
                                            v-if="canBeRelisted"
                                            class="mb-3"
                                        >
                                            <button
                                                class="color-mid-grey text-underline px-0"
                                                type="button"
                                                @click="$refs.modalRelistProperty.show()"
                                            >
                                                Relist Property
                                            </button>
                                        </div>
                                        <div>
                                            <button
                                                class="color-mid-grey text-underline px-0"
                                                type="button"
                                                @click="$refs.modalDeleteProperty.show()"
                                            >
                                                Delete Property
                                            </button>
                                        </div>
                                    </div>
                                </div>
                            </div>
                            <!-- END Property actions -->
                        </div>
                    </div>
                    <!-- END Actions -->
                </b-col>
            </b-row>
            <!-- END Package / Actions -->

            <!-- Offers -->
            <div
                v-if="property.transaction_type === 'sale' && property.offers && property.offers.length"
                class="card"
            >
                <!-- Offers heading -->
                <div class="d-flex align-items-center justify-content-between px-4 py-5">
                    <h3>Your Offers</h3>
                </div>
                <!-- END Offers heading -->

                <!-- Offers table -->
                <div
                    id="table-offers"
                    class="table-flex table-flex--plain table-flex--cell-1-md table-flex--cell-2-sm table-flex--cell-6-md"
                >
                    <div class="table-flex--header">
                        <div />
                        <div>ID</div>
                        <div>Date</div>
                        <div>Offer</div>
                        <div>Name</div>
                        <div>Status</div>
                        <div />
                    </div>
                    <div
                        v-for="offer in property.offers"
                        :key="offer.id"
                    >
                        <div><span class="label-type label-type--sell">Offer</span></div>
                        <div><span class="text-bold color-dark-grey">#{{ offer.id }}</span></div>
                        <div>{{ offer.created_at | moment('L') }}</div>
                        <div>{{ offer.amount | price(false) }}</div>
                        <div>{{ offer.name }}</div>
                        <div>{{ offer.status.display_name }}</div>
                        <div class="justify-content-end">
                            <button
                                class="btn btn--outline btn--nomin btn--sm"
                                @click="showOffer(offer.id)"
                            >
                                View
                            </button>
                        </div>
                    </div>
                </div>
                <!-- END Offers table -->
            </div>

            <!-- Viewings -->
            <div
                v-if="viewings && viewings.length"
                class="card"
            >
                <!-- Viewings heading -->
                <div class="d-flex align-items-center justify-content-between px-4 py-5">
                    <h3>Viewings</h3>
                </div>
                <!-- END Viewings heading -->

                <!-- Viewings table -->
                <div
                    id="table-viewings"
                    class="table-flex table-flex--plain table-flex--cell-1-md table-flex--cell-2-sm table-flex--cell-6-md"
                >
                    <div class="table-flex--header">
                        <div />
                        <div>ID</div>
                        <div>Name</div>
                        <div>Viewing Date</div>
                        <div>Request Message</div>
                        <div>Feedback</div>
                        <div>Status</div>
                        <div>Requested at</div>
                        <div />
                    </div>
                    <div
                        v-for="viewing in viewings"
                        :key="viewing.id"
                    >
                        <div><span class="label-type label-type--sell">Viewing</span></div>
                        <div><span class="text-bold color-dark-grey">#{{ viewing.id }}</span></div>
                        <div>{{ viewing.user.first_name }} {{ viewing.user.last_name }}</div>
                        <div>{{ viewing.start_at | moment('L') }}</div>
                        <div>{{ viewing.request_message }}</div>
                        <div>{{ viewing.feedback }}</div>
                        <div>{{ viewing.status.display_name }}</div>
                        <div>{{ viewing.created_at | moment('L') }}</div>
                        <div class="justify-content-end">
                            <button
                                class="btn btn--outline btn--nomin btn--sm"
                                @click="showViewing(viewing.id)"
                            >
                                View
                            </button>
                        </div>
                    </div>
                </div>
                <!-- END Viewings table -->
            </div>

            <!-- START accordion -->
            <div
                id="accordion"
                role="tablist"
            >
                <!-- START accordion 1 - AML Check -->
                <b-card-header
                    header-tag="header"
                    role="tab"
                >
                    <h2
                        v-b-toggle.accordion1
                        class="mb-0 accordion-toggle"
                    >
                        <span>AML Check</span>
                        <span class="d-flex align-items-center">
                            <ValidationCheck :status="validationStatus.aml" />
                            <SVGSprite
                                name="add"
                                class="accordion-toggle__icon accordion-toggle__icon-plus"
                            />
                            <SVGSprite
                                name="remove"
                                class="accordion-toggle__icon accordion-toggle__icon-minus"
                            />
                        </span>
                    </h2>
                </b-card-header>

                <b-collapse
                    id="accordion1"
                    role="tabpanel"
                    accordion="accordion"
                >
                    <b-card-body>
                        <AMLCheck />
                    </b-card-body>
                </b-collapse>
                <!-- END accordion 1 - AML Check -->

                <!-- START accordion 2 - Property details -->
                <b-card-header
                    header-tag="header"
                    role="tab"
                >
                    <h2
                        v-b-toggle.accordion2
                        class="mb-0 accordion-toggle"
                    >
                        <span>Property Details</span>
                        <span class="d-flex align-items-center">
                            <ValidationCheck :status="validationStatus.details" />
                            <SVGSprite
                                name="add"
                                class="accordion-toggle__icon accordion-toggle__icon-plus"
                            />
                            <SVGSprite
                                name="remove"
                                class="accordion-toggle__icon accordion-toggle__icon-minus"
                            />
                        </span>
                    </h2>
                </b-card-header>

                <b-collapse
                    id="accordion2"
                    role="tabpanel"
                    accordion="accordion"
                >
                    <b-card-body>
                        <b-row>
                            <b-col
                                cols="12"
                                lg="8"
                                class="mb-5"
                            >
                                <p>All of the details you have entered for this property can be viewed within this section. Any of the information you have given us can be edited here.</p>
                            </b-col>
                        </b-row>
                        <b-row>
                            <b-col
                                cols="12"
                                lg="6"
                            >
                                <!-- Field: Search address -->
                                <div class="field-wrap">
                                    <h4 class="mb-3 color-mid-grey text-bold">
                                        Address Lookup
                                    </h4>
                                    <label
                                        for="field-search"
                                        class="sr-only"
                                    >
                                        Postcode
                                    </label>
                                    <div class="field-wrap__input field-wrap__input--prepend field-wrap__input--prepend-fill">
                                        <input
                                            id="field-search"
                                            v-model="postcodeSearch"
                                            type="search"
                                            name="field-search"
                                            placeholder="Postcode or Location e.g. London"
                                        >
                                        <div class="field-wrap__input-icon">
                                            <SVGSprite
                                                name="search"
                                                class="form-icon"
                                            />
                                        </div>
                                    </div>
                                </div>
                                <button
                                    class="btn btn--secondary mb-5"
                                    @click.prevent="searchAddress()"
                                >
                                    Search Address
                                </button>
                                <!-- END Field: Search address -->

                                <!-- Field(s): Address -->
                                <div class="field-wrap">
                                    <!-- Field: House Name / Number -->
                                    <h4 class="mb-3 color-mid-grey text-bold">
                                        <span
                                            v-b-tooltip.hover
                                            title="Required"
                                        >
                                            Address
                                            <span class="required">*</span>
                                        </span>
                                    </h4>
                                    <label
                                        for="field-house-name-number"
                                        class="sr-only"
                                    >
                                        House Name or Number
                                    </label>
                                    <div class="field-wrap__input mb-4">
                                        <input
                                            id="field-house-name-number"
                                            v-model="propertyForm.house_name_number"
                                            v-validate="{ required: true }"
                                            :class="{ 'has-error': showErrors && errors.has('field-house-name-number') }"
                                            type="text"
                                            name="field-house-name-number"
                                            placeholder="House Name or Number"
                                        >
                                    </div>
                                    <div
                                        v-if="showErrors && errors.has('field-house-name-number')"
                                        class="py-3"
                                    >
                                        <div class="d-inline-block alert-error p-3 rounded-corners">
                                            Please enter your house name or number
                                        </div>
                                    </div>
                                    <!-- END Field: House Name / Number -->

                                    <!-- Field: Address Line 1 -->
                                    <label
                                        for="field-address-1"
                                        class="sr-only"
                                    >
                                        Address Line 1
                                    </label>
                                    <div class="field-wrap__input mb-4">
                                        <input
                                            id="field-address-1"
                                            :key="'address'"
                                            v-model="propertyForm.address"
                                            v-validate="{ required: true, regex: /[a-zA-Z0-9\s]+/ }"
                                            :class="{ 'has-error': showErrors && errors.has('field-address-1') }"
                                            type="text"
                                            name="field-address-1"
                                            placeholder="Address Line 1"
                                        >
                                    </div>
                                    <div
                                        v-if="showErrors && errors.has('field-address-1')"
                                        class="py-3"
                                    >
                                        <div class="d-inline-block alert-error p-3 rounded-corners">
                                            Please enter the property address
                                        </div>
                                    </div>
                                    <!-- END Field: Address Line 1 -->

                                    <!-- Field: Address Line 2 -->
                                    <label
                                        for="field-address-2"
                                        class="sr-only"
                                    >
                                        Address Line 2
                                    </label>
                                    <div class="field-wrap__input mb-4">
                                        <input
                                            id="field-address-2"
                                            :key="'address2'"
                                            v-model="propertyForm.address2"
                                            v-validate="{ regex: /[a-zA-Z0-9\s]+/ }"
                                            :class="{ 'has-error': showErrors && errors.has('field-address-2') }"
                                            type="text"
                                            name="field-address-2"
                                            placeholder="Address Line 2"
                                        >
                                    </div>
                                    <div
                                        v-if="showErrors && errors.has('field-address-2')"
                                        class="py-3"
                                    >
                                        <div class="d-inline-block alert-error p-3 rounded-corners">
                                            Please enter a valid property address
                                        </div>
                                    </div>
                                    <!-- END Field: Address Line 2 -->
                                    <!-- Field: Town / City -->
                                    <label
                                        for="field-town-city"
                                        class="sr-only"
                                    >
                                        Town / City
                                    </label>
                                    <div class="field-wrap__input mb-4">
                                        <input
                                            id="field-town-city"
                                            :key="'town-city'"
                                            v-model="propertyForm.town"
                                            v-validate="{ required: true, alpha_spaces: true }"
                                            :class="{ 'has-error': showErrors && errors.has('field-town-city') }"
                                            type="text"
                                            name="field-town-city"
                                            placeholder="Town / City"
                                        >
                                    </div>
                                    <div
                                        v-if="showErrors && errors.has('field-town-city')"
                                        class="py-3"
                                    >
                                        <div class="d-inline-block alert-error p-3 rounded-corners">
                                            Please enter a valid town or city
                                        </div>
                                    </div>
                                    <!-- END Field: Town / City -->
                                    <div class="row row--sm">
                                        <div class="col-12 col-lg-6">
                                            <!-- Field: County -->
                                            <label
                                                for="field-county"
                                                class="sr-only"
                                            >
                                                County
                                            </label>
                                            <div class="field-wrap__input mb-4">
                                                <input
                                                    id="field-county"
                                                    :key="'county'"
                                                    v-model="propertyForm.county"
                                                    type="text"
                                                    name="field-county"
                                                    placeholder="County"
                                                >
                                            </div>
                                            <!-- END Field: County -->
                                        </div>
                                        <div class="col-12 col-lg-6">
                                            <!-- Field: Postcode -->
                                            <label
                                                for="field-postcode"
                                                class="sr-only"
                                            >
                                                Postcode
                                            </label>
                                            <div class="field-wrap__input mb-4">
                                                <input
                                                    id="field-postcode"
                                                    :key="'postcode'"
                                                    v-model="propertyForm.postcode"
                                                    v-validate="{ required: true, regex: /[a-zA-Z0-9\s]+/ }"
                                                    :class="{ 'has-error': showErrors && errors.has('field-postcode') }"
                                                    type="text"
                                                    name="field-postcode"
                                                    placeholder="Postcode"
                                                >
                                            </div>
                                            <div
                                                v-if="showErrors && errors.has('field-postcode')"
                                                class="py-3"
                                            >
                                                <div class="d-inline-block alert-error p-3 rounded-corners">
                                                    Please enter a valid postcode
                                                </div>
                                            </div>
                                            <!-- END Field: Postcode -->
                                        </div>
                                    </div>
                                </div>
                                <!-- END Field(s): Address -->
                            </b-col>
                            <b-col
                                cols="12"
                                lg="6"
                            >
                                <!-- Field: Country -->
                                <div class="field-wrap">
                                    <h4 class="mb-3 color-mid-grey text-bold">
                                        <span
                                            v-b-tooltip.hover
                                            title="Required"
                                        >
                                            Country
                                            <span class="required">*</span>
                                        </span>
                                    </h4>
                                    <label
                                        for="field-country"
                                        class="sr-only"
                                    >
                                        Country
                                    </label>
                                    <div class="field-wrap__input field-wrap__input--select mb-4">
                                        <select
                                            id="field-country"
                                            :key="'country'"
                                            v-model="propertyForm.country"
                                            v-validate="{ required: true, alpha_spaces: true }"
                                            :class="{ 'has-error': showErrors && errors.has('field-country') }"
                                            name="field-country"
                                        >
                                            <option
                                                :value="null"
                                                disabled
                                                selected
                                            >
                                                Select a value
                                            </option>
                                            <option>England</option>
                                            <option>Wales</option>
                                            <option>Scotland</option>
                                        </select>
                                    </div>
                                    <div
                                        v-if="showErrors && errors.has('field-country')"
                                        class="py-3"
                                    >
                                        <div class="d-inline-block alert-error p-3 rounded-corners">
                                            Please enter a valid country
                                        </div>
                                    </div>
                                </div>
                                <!-- END Field: Country -->

                                <!-- Field: Property type -->
                                <div class="field-wrap">
                                    <h4 class="mb-3 color-mid-grey text-bold">
                                        <span
                                            v-b-tooltip.hover
                                            title="Required"
                                        >
                                            Property Type
                                            <span class="required">*</span>
                                        </span>
                                    </h4>
                                    <label
                                        for="field-type"
                                        class="sr-only"
                                    >
                                        Property Type
                                    </label>
                                    <div class="field-wrap__input field-wrap__input--select mb-4">
                                        <select
                                            id="field-type"
                                            :key="'propertyType'"
                                            v-model="propertyForm.property_type_id"
                                            v-validate="{ required: true, included: propertyTypes.map(item => item.id) }"
                                            :class="{ 'has-error': showErrors && errors.has('field-type') }"
                                            name="field-type"
                                        >
                                            <option
                                                :value="null"
                                                disabled
                                                selected
                                            >
                                                Select a value
                                            </option>
                                            <option
                                                v-for="type in propertyTypes"
                                                :key="type.id"
                                                :value="type.id"
                                            >
                                                {{ type.name }}
                                            </option>
                                        </select>
                                    </div>
                                    <div
                                        v-if="showErrors && errors.has('field-type')"
                                        class="py-3"
                                    >
                                        <div class="d-inline-block alert-error p-3 rounded-corners">
                                            Please select a valid property type
                                        </div>
                                    </div>
                                </div>
                                <!-- END Field: Property type -->

                                <!-- Field: Date -->
                                <div class="field-wrap">
                                    <h4 class="mb-3 color-mid-grey text-bold">
                                        <span
                                            v-b-tooltip.hover
                                            title="Required"
                                        >
                                            Date available
                                            <span class="required">*</span>
                                        </span>
                                    </h4>
                                    <label
                                        for="field-county"
                                        class="sr-only"
                                    >
                                        Date Available
                                    </label>
                                    <div class="field-wrap__input mb-4">
                                        <input
                                            id="field-date"
                                            :key="'dateAvailable'"
                                            v-model="propertyForm.date_available"
                                            v-validate="{ required: true, date_format: 'yyyy-MM-dd' }"
                                            :class="{ 'has-error': showErrors && errors.has('field-date') }"
                                            type="date"
                                            name="field-date"
                                            placeholder="Date available"
                                        >
                                    </div>
                                    <div
                                        v-if="showErrors && errors.has('field-date')"
                                        class="py-3"
                                    >
                                        <div class="d-inline-block alert-error p-3 rounded-corners">
                                            Please enter a valid date available
                                        </div>
                                    </div>
                                </div>
                                <!-- END Field: Date -->
                            </b-col>
                        </b-row>
                    </b-card-body>
                </b-collapse>
                <!-- END accordion 2 - Property details -->

                <!-- START accordion 3 - Ownership -->
                <b-card-header
                    header-tag="header"
                    role="tab"
                >
                    <h2
                        v-b-toggle.accordion3
                        class="mb-0 accordion-toggle"
                    >
                        <span>Ownership</span>
                        <span class="d-flex align-items-center">
                            <ValidationCheck :status="validationStatus.ownership" />
                            <SVGSprite
                                name="add"
                                class="accordion-toggle__icon accordion-toggle__icon-plus"
                            />
                            <SVGSprite
                                name="remove"
                                class="accordion-toggle__icon accordion-toggle__icon-minus"
                            />
                        </span>
                    </h2>
                </b-card-header>

                <b-collapse
                    id="accordion3"
                    accordion="accordion"
                    role="tabpanel"
                >
                    <b-card-body>
                        <b-row>
                            <b-col
                                cols="12"
                                class="mb-5"
                            >
                                <!-- Field: Ownership -->
                                <div class="field-wrap mb-0">
                                    <h4 class="mb-3 color-mid-grey text-bold">
                                        <span
                                            v-b-tooltip.hover
                                            title="Required"
                                        >
                                            Who owns this property?
                                            <span class="required">*</span>
                                        </span>
                                    </h4>
                                    <div class="row mt-4">
                                        <div class="col-12 col-lg-4">
                                            <div class="field-wrap__input field-wrap__input--radio">
                                                <input
                                                    id="field-ownership-1"
                                                    :key="'userOwnershipTrue'"
                                                    v-model="propertyForm.user_ownership"
                                                    :value="true"
                                                    type="radio"
                                                    name="field-ownership"
                                                >
                                                <label for="field-ownership-1"><span class="check-element" />I own this property</label>
                                            </div>
                                        </div>
                                        <div class="col-12 col-lg-4">
                                            <div class="field-wrap__input field-wrap__input--radio">
                                                <input
                                                    id="field-ownership-2"
                                                    :key="'userOwnershipFalse'"
                                                    v-model="propertyForm.user_ownership"
                                                    value="with_others"
                                                    type="radio"
                                                    name="field-ownership"
                                                >
                                                <label for="field-ownership-2"><span class="check-element" />I own this property with others</label>
                                            </div>
                                        </div>
                                        <div class="col-12 col-lg-4">
                                            <div class="field-wrap__input field-wrap__input--radio">
                                                <input
                                                    id="field-ownership-3"
                                                    :key="'userOwnershipFalse'"
                                                    v-model="propertyForm.user_ownership"
                                                    :value="false"
                                                    type="radio"
                                                    name="field-ownership"
                                                >
                                                <label for="field-ownership-3"><span class="check-element" />Another seller e.g. on behalf of someone else*</label>
                                            </div>
                                        </div>
                                    </div>
                                    <p class="mb-0 mt-4 color-mid-grey p-sm text-italic">
                                        *We cannot advertise on behalf of other selling agents
                                    </p>
                                </div>
                                <!-- END Field: Ownership -->
                            </b-col>
                        </b-row>

                        <b-row v-if="propertyForm.user_ownership !== true && propertyForm.user_ownership !== null">
                            <b-col
                                cols="12"
                            >
                                <p class="mb-5">
                                    If you would like to add another owner to this property, please click the below button and follow the instructions.
                                </p>
                                <OwnersRepeater
                                    v-for="(item, index) in owners"
                                    :key="item.key"
                                    :index="index"
                                    :owner="item"
                                    :owners="owners"
                                    :existing-owners="selectableOwners"
                                    @output="owner => owners[index] = owner"
                                    @delete="handleOwnerRemove"
                                />

                                <button
                                    class="btn btn--secondary"
                                    @click.prevent="addOwner()"
                                >
                                    Add Owner
                                </button>
                            </b-col>
                        </b-row>
                    </b-card-body>
                </b-collapse>
                <!-- END accordion 3 - Joint ownership -->

                <!-- START accordion 4 - Feature Points -->
                <b-card-header
                    header-tag="header"
                    role="tab"
                >
                    <h2
                        v-b-toggle.accordion4
                        class="mb-0 accordion-toggle"
                    >
                        <span>Feature Points</span>
                        <span class="d-flex align-items-center">
                            <ValidationCheck :status="validationStatus.featurePoints" />
                            <SVGSprite
                                name="add"
                                class="accordion-toggle__icon accordion-toggle__icon-plus"
                            />
                            <SVGSprite
                                name="remove"
                                class="accordion-toggle__icon accordion-toggle__icon-minus"
                            />
                        </span>
                    </h2>
                </b-card-header>

                <b-collapse
                    id="accordion4"
                    accordion="accordion"
                    role="tabpanel"
                >
                    <b-card-body>
                        <b-row class="mb-4">
                            <b-col
                                cols="12"
                                lg="6"
                            >
                                <!-- Field: Bedrooms -->
                                <div class="field-wrap">
                                    <h4 class="mb-3 color-mid-grey text-bold">
                                        <span
                                            v-b-tooltip.hover
                                            title="Required"
                                        >
                                            Bedrooms
                                            <span class="required">*</span>
                                        </span>
                                    </h4>
                                    <label
                                        for="field-bedrooms"
                                        class="sr-only"
                                    >
                                        Number of Bedrooms
                                    </label>
                                    <div class="field-wrap__input field-wrap__input--select mb-4">
                                        <select
                                            id="field-bedrooms"
                                            v-model="propertyForm.bedrooms"
                                            v-validate="{ required: true, numeric: true }"
                                            :class="{ 'has-error': showErrors && errors.has('field-bedrooms') }"
                                            name="field-bedrooms"
                                        >
                                            <option
                                                :value="null"
                                                disabled
                                                selected
                                            >
                                                Select a value
                                            </option>
                                            <option :value="0">
                                                0
                                            </option>
                                            <option
                                                v-for="n in 6"
                                                :key="n"
                                                :value="n"
                                            >
                                                {{ n >= 6 ? '6+' : n }}
                                            </option>
                                        </select>
                                    </div>
                                    <div
                                        v-if="showErrors && errors.has('field-bedrooms')"
                                        class="py-3"
                                    >
                                        <div class="d-inline-block alert-error p-3 rounded-corners">
                                            Please select the number of bedrooms
                                        </div>
                                    </div>
                                </div>
                                <!-- END Field: Bedrooms -->

                                <!-- Field: Baths -->
                                <div class="field-wrap">
                                    <h4 class="mb-3 color-mid-grey text-bold">
                                        <span
                                            v-b-tooltip.hover
                                            title="Required"
                                        >
                                            Baths
                                            <span class="required">*</span>
                                        </span>
                                    </h4>
                                    <label
                                        for="field-baths"
                                        class="sr-only"
                                    >
                                        Number of Baths
                                    </label>
                                    <div class="field-wrap__input field-wrap__input--select mb-4">
                                        <select
                                            id="field-baths"
                                            v-model="propertyForm.bathrooms"
                                            v-validate="{ required: true, numeric: true }"
                                            :class="{ 'has-error': showErrors && errors.has('field-baths') }"
                                            name="field-baths"
                                        >
                                            <option
                                                :value="null"
                                                disabled
                                                selected
                                            >
                                                Select a value
                                            </option>
                                            <option :value="0">
                                                0
                                            </option>
                                            <option
                                                v-for="n in 5"
                                                :key="n"
                                                :value="n"
                                            >
                                                {{ n }}
                                            </option>
                                        </select>
                                    </div>
                                    <div
                                        v-if="showErrors && errors.has('field-baths')"
                                        class="py-3"
                                    >
                                        <div class="d-inline-block alert-error p-3 rounded-corners">
                                            Please select the number of bathrooms
                                        </div>
                                    </div>
                                </div>
                                <!-- END Field: Baths -->

                                <!-- Field: Floors -->
                                <div class="field-wrap">
                                    <h4 class="mb-3 color-mid-grey text-bold">
                                        <span
                                            v-b-tooltip.hover
                                            title="Required"
                                        >
                                            Floors
                                            <span class="required">*</span>
                                        </span>
                                    </h4>
                                    <label
                                        for="field-floors"
                                        class="sr-only"
                                    >
                                        Number of Floors
                                    </label>
                                    <div class="field-wrap__input field-wrap__input--select mb-4">
                                        <select
                                            id="field-floors"
                                            v-model="propertyForm.floors"
                                            v-validate="{ required: true, numeric: true }"
                                            :class="{ 'has-error': showErrors && errors.has('field-floors') }"
                                            name="field-floors"
                                        >
                                            <option
                                                :value="null"
                                                disabled
                                                selected
                                            >
                                                Select a value
                                            </option>
                                            <option :value="0">
                                                0
                                            </option>
                                            <option
                                                v-for="n in 6"
                                                :key="n"
                                                :value="n"
                                            >
                                                {{ n >= 6 ? '6+' : n }}
                                            </option>
                                        </select>
                                    </div>
                                    <div
                                        v-if="showErrors && errors.has('field-floors')"
                                        class="py-3"
                                    >
                                        <div class="d-inline-block alert-error p-3 rounded-corners">
                                            Please select the number of floors
                                        </div>
                                    </div>
                                </div>
                                <!-- END Field: Floors -->
                            </b-col>
                            <b-col
                                cols="12"
                                lg="6"
                            >
                                <!-- Field: Reception Rooms -->
                                <div class="field-wrap">
                                    <h4 class="mb-3 color-mid-grey text-bold">
                                        <span
                                            v-b-tooltip.hover
                                            title="Required"
                                        >
                                            Reception Rooms
                                            <span class="required">*</span>
                                        </span>
                                    </h4>
                                    <label
                                        for="field-reception"
                                        class="sr-only"
                                    >
                                        Number of Reception Rooms
                                    </label>
                                    <div class="field-wrap__input field-wrap__input--select mb-4">
                                        <select
                                            id="field-reception"
                                            v-model="propertyForm.receptions"
                                            v-validate="{ required: true, numeric: true }"
                                            :class="{ 'has-error': showErrors && errors.has('field-reception') }"
                                            name="field-reception"
                                        >
                                            <option
                                                :value="null"
                                                disabled
                                                selected
                                            >
                                                Select a value
                                            </option>
                                            <option :value="0">
                                                0
                                            </option>
                                            <option
                                                v-for="n in 6"
                                                :key="n"
                                                :value="n"
                                            >
                                                {{ n >= 6 ? '6+' : n }}
                                            </option>
                                        </select>
                                    </div>
                                    <div
                                        v-if="showErrors && errors.has('field-reception')"
                                        class="py-3"
                                    >
                                        <div class="d-inline-block alert-error p-3 rounded-corners">
                                            Please select the number of reception rooms
                                        </div>
                                    </div>
                                </div>
                                <!-- END Field: Reception Rooms -->

                                <!-- Field: Garage -->
                                <div class="field-wrap">
                                    <h4 class="mb-3 color-mid-grey text-bold">
                                        <span
                                            v-b-tooltip.hover
                                            title="Required"
                                        >
                                            Garage
                                            <span class="required">*</span>
                                        </span>
                                    </h4>
                                    <label
                                        for="field-garages"
                                        class="sr-only"
                                    >
                                        Number of Garages
                                    </label>
                                    <div class="field-wrap__input field-wrap__input--select mb-4">
                                        <select
                                            id="field-garages"
                                            v-model="propertyForm.garages"
                                            v-validate="{ required: true, numeric: true }"
                                            :class="{ 'has-error': showErrors && errors.has('field-garages') }"
                                            name="field-garages"
                                        >
                                            <option
                                                :value="null"
                                                disabled
                                                selected
                                            >
                                                Select a value
                                            </option>
                                            <option value="0">
                                                None
                                            </option>
                                            <option value="1">
                                                Single
                                            </option>
                                            <option value="2">
                                                Double
                                            </option>
                                        </select>
                                    </div>
                                    <div
                                        v-if="showErrors && errors.has('field-garages')"
                                        class="py-3"
                                    >
                                        <div class="d-inline-block alert-error p-3 rounded-corners">
                                            Please enter the number of garages
                                        </div>
                                    </div>
                                </div>
                                <!-- END Field: Garage -->

                                <!-- Field: Area -->
                                <div class="field-wrap mb-0">
                                    <h4 class="mb-3 color-mid-grey text-bold">
                                        <span
                                            v-b-tooltip.hover
                                            title="Required"
                                        >
                                            Area
                                            <span class="required">*</span>
                                        </span>
                                    </h4>
                                    <label
                                        for="field-area"
                                        class="sr-only"
                                    >
                                        Area
                                    </label>
                                    <div class="field-wrap__input mb-4">
                                        <input
                                            id="field-area"
                                            v-model="propertyForm.area"
                                            type="text"
                                            name="field-area"
                                            placeholder="e.g. 1200 sqft"
                                        >
                                    </div>
                                </div>
                                <!-- END Field: Area -->
                            </b-col>
                        </b-row>
                        <b-row>
                            <b-col
                                cols="12"
                                lg="8"
                                class="mb-5"
                            >
                                <p>The partner websites within our network e.g. Rightmove will display feature points relating to your property. This is your chance to list what you think are the best features of your home, examples of which might be a new bathroom, a new kitchen or private parking.</p>
                            </b-col>
                        </b-row>
                        <b-row v-if="features">
                            <b-col
                                cols="12"
                            >
                                <b-row>
                                    <b-col
                                        v-for="feature in sortedFeatures"
                                        :key="feature.key"
                                        cols="12"
                                        class="mb-5"
                                    >
                                        <b-row class="w-100">
                                            <b-col
                                                cols="12"
                                                lg="8"
                                            >
                                                <div class="field-wrap mr-lg-4">
                                                    <div class="field-wrap__input mb-4">
                                                        <input
                                                            :id="`field-feature-${feature.key}`"
                                                            v-model="feature.content"
                                                            v-validate="{ required: true, max: 191 }"
                                                            :class="{ 'has-error': showErrors && errors.has(`field-feature-${feature.key}`) }"
                                                            :name="`field-feature-${feature.key}`"
                                                            type="text"
                                                        >
                                                    </div>
                                                    <div
                                                        v-if="showErrors && errors.has(`field-feature-${feature.key}`)"
                                                        class="py-3"
                                                    >
                                                        <div class="d-inline-block alert-error p-3 rounded-corners">
                                                            Please enter a feature
                                                        </div>
                                                    </div>
                                                </div>
                                            </b-col>
                                            <b-col
                                                cols="12"
                                                lg="4"
                                                class="pt-lg-3 d-flex justify-content-end justify-content-lg-start"
                                            >
                                                <button
                                                    :class="{ 'disabled': feature.order <= 1 }"
                                                    :disabled="feature.order <= 1"
                                                    class="btn btn--secondary btn--nomin btn--sm mx-3 btn--feature d-flex align-items-center justify-content-center"
                                                    @click="featureMoveUp(feature)"
                                                >
                                                    <SVGSprite
                                                        class="btn-icon-feature"
                                                        name="arrow-up"
                                                    />
                                                </button>
                                                <button
                                                    :class="{ 'disabled': feature.order >= sortedFeatures.length }"
                                                    :disabled="feature.order >= sortedFeatures.length"
                                                    class="btn btn--secondary btn--nomin btn--sm mx-3 btn--feature d-flex align-items-center justify-content-center"
                                                    @click="featureMoveDown(feature)"
                                                >
                                                    <SVGSprite
                                                        class="btn-icon-feature"
                                                        name="arrow-down"
                                                    />
                                                </button>
                                                <button
                                                    v-if="sortedFeatures.length > 3"
                                                    class="btn btn--primary btn--nomin btn--sm mx-3 btn--feature d-flex align-items-center justify-content-center"
                                                    @click="featureRemove(feature)"
                                                >
                                                    <SVGSprite
                                                        class="btn-icon-feature"
                                                        name="close"
                                                    />
                                                </button>
                                            </b-col>
                                        </b-row>
                                    </b-col>
                                </b-row>
                            </b-col>
                        </b-row>

                        <button
                            type="button"
                            class="btn btn--secondary"
                            @click="featureAdd(1)"
                        >
                            Add Feature
                        </button>
                    </b-card-body>
                </b-collapse>
                <!-- END accordion 4 - Feature Points -->

                <!-- START accordion 5 - Sale -->
                <div v-if="property.transaction_type === 'sale'">
                    <b-card-header
                        header-tag="header"
                        role="tab"
                    >
                        <h2
                            v-b-toggle.accordion5
                            class="mb-0 accordion-toggle"
                        >
                            <span>Sale</span>
                            <span class="d-flex align-items-center">
                                <ValidationCheck :status="validationStatus.sale" />
                                <SVGSprite
                                    name="add"
                                    class="accordion-toggle__icon accordion-toggle__icon-plus"
                                />
                                <SVGSprite
                                    name="remove"
                                    class="accordion-toggle__icon accordion-toggle__icon-minus"
                                />
                            </span>
                        </h2>
                    </b-card-header>
                    <b-collapse
                        id="accordion5"
                        accordion="accordion"
                        role="tabpanel"
                    >
                        <b-card-body>
                            <b-row>
                                <b-col
                                    cols="12"
                                    lg="7"
                                    class="mb-5"
                                >
                                    <!-- Sale heading -->
                                    <p>In this section you can set your initial asking price. You can amend your asking price 24/7 via your online account and we are always on hand to offer guidance</p>
                                    <!-- END Sale heading -->
                                </b-col>
                                <b-col
                                    cols="12"
                                    lg="5"
                                    class="mb-5 d-lg-flex align-items-lg-center justify-content-lg-end"
                                >
                                    <!-- Field: Best Guide Price -->
                                    <div class="w-100">
                                        <div class="field-wrap">
                                            <h4 class="mb-3 color-mid-grey text-bold">
                                                Find the best guide price based on postcode
                                            </h4>
                                            <label
                                                for="field-search"
                                                class="sr-only"
                                            >
                                                Postcode
                                            </label>
                                            <div class="field-wrap__input field-wrap__input--prepend field-wrap__input--prepend-fill mb-4">
                                                <input
                                                    id="field-guide"
                                                    v-model="guidePostcode"
                                                    type="search"
                                                    name="field-guide"
                                                    placeholder="Enter postcode"
                                                    @keyup.enter="getInitialPrices()"
                                                >
                                                <div class="field-wrap__input-icon">
                                                    <SVGSprite
                                                        name="search"
                                                        class="form-icon"
                                                    />
                                                </div>
                                                <b-btn
                                                    class="zoopla-button"
                                                    @click="getInitialPrices()"
                                                >
                                                    <SVGSprite
                                                        name="zooplapower"
                                                        class="zoopla-icon"
                                                    />
                                                </b-btn>
                                            </div>
                                        </div>
                                    </div>
                                    <!-- END Field: Best Guide Price -->
                                </b-col>
                            </b-row>
                            <b-row>
                                <b-col
                                    cols="12"
                                    lg="6"
                                >
                                    <b-row>
                                        <b-col
                                            cols="12"
                                            lg="6"
                                        >
                                            <!-- Field: Qualifier -->
                                            <h4 class="mb-3 color-mid-grey text-bold">
                                                <span
                                                    v-b-tooltip.hover
                                                    title="Required"
                                                >
                                                    Price Description
                                                    <span class="required">*</span>
                                                </span>
                                            </h4>
                                            <div class="field-wrap">
                                                <label
                                                    for="field-qualifier"
                                                    class="sr-only"
                                                >
                                                    Price Description
                                                </label>
                                                <div class="field-wrap__input field-wrap__input--select">
                                                    <select
                                                        id="field-qualifier"
                                                        :key="'qualifier'"
                                                        v-model="propertyForm.price_prefix"
                                                        v-validate="{ required: true, included: qualifiers.map(item => item.name) }"
                                                        :class="{ 'has-error': showErrors && errors.has('field-qualifier') }"
                                                        name="field-qualifier"
                                                    >
                                                        <option
                                                            :value="null"
                                                            disabled
                                                            selected
                                                        >
                                                            Select a value
                                                        </option>
                                                        <option
                                                            v-for="qualifier in qualifiers"
                                                            :key="qualifier.id"
                                                        >
                                                            {{ qualifier.name }}
                                                        </option>
                                                    </select>
                                                </div>
                                                <div
                                                    v-if="showErrors && errors.has('field-qualifier')"
                                                    class="py-3"
                                                >
                                                    <div class="d-inline-block alert-error p-3 rounded-corners">
                                                        Please select a price description
                                                    </div>
                                                </div>
                                            </div>
                                            <!-- END Field: Qualifier -->
                                        </b-col>
                                        <b-col
                                            cols="12"
                                            lg="6"
                                        >
                                            <!-- Field: Asking Price -->
                                            <h4 class="mb-3 color-mid-grey text-bold">
                                                <span
                                                    v-b-tooltip.hover
                                                    title="Required"
                                                >
                                                    Asking Price
                                                    <span class="required">*</span>
                                                </span>
                                            </h4>
                                            <div class="field-wrap">
                                                <label
                                                    for="field-asking"
                                                    class="sr-only"
                                                >
                                                    Asking Price
                                                </label>
                                                <div class="field-wrap__input field-wrap__input--prepend field-wrap__input--prepend-fill">
                                                    <input
                                                        id="field-asking"
                                                        v-model="propertyForm.price"
                                                        v-validate="{ required: true, decimal: 2, min_value: 0 }"
                                                        :class="{ 'has-error': showErrors && errors.has('field-asking') }"
                                                        type="number"
                                                        name="field-asking"
                                                        placeholder="0"
                                                    >
                                                    <div class="field-wrap__input-icon">
                                                        <SVGSprite
                                                            name="gbp"
                                                            class="form-icon"
                                                        />
                                                    </div>
                                                </div>
                                                <div
                                                    v-if="showErrors && errors.has('field-asking')"
                                                    class="py-3"
                                                >
                                                    <div class="d-inline-block alert-error p-3 rounded-corners">
                                                        Please enter a valid price
                                                    </div>
                                                </div>
                                            </div>
                                            <!-- END Field: Asking Price -->
                                        </b-col>
                                    </b-row>
                                </b-col>
                                <b-col
                                    v-if="propertyForm.tenure_id === 2"
                                    cols="12"
                                    lg="6"
                                >
                                    <!-- Field: Ground Rent -->
                                    <h4 class="mb-3 color-mid-grey text-bold">
                                        <span
                                            v-b-tooltip.hover
                                            title="Required"
                                        >
                                            Ground Rent
                                            <span class="required">*</span>
                                        </span>
                                    </h4>
                                    <div class="field-wrap">
                                        <label
                                            for="field-asking"
                                            class="sr-only"
                                        >
                                            Ground Rent
                                        </label>
                                        <div class="field-wrap__input field-wrap__input--prepend field-wrap__input--prepend-fill">
                                            <input
                                                id="field-ground"
                                                v-model="propertyForm.ground_rent"
                                                v-validate="{ required: true, decimal: 2, min_value: 0 }"
                                                :class="{ 'has-error': showErrors && errors.has('field-ground') }"
                                                type="text"
                                                name="field-ground"
                                                placeholder="i.e. 25 per month"
                                            >
                                            <div class="field-wrap__input-icon">
                                                <SVGSprite
                                                    name="gbp"
                                                    class="form-icon"
                                                />
                                            </div>
                                        </div>
                                        <div
                                            v-if="showErrors && errors.has('field-ground')"
                                            class="py-3"
                                        >
                                            <div class="d-inline-block alert-error p-3 rounded-corners">
                                                Please enter a valid ground rent
                                            </div>
                                        </div>
                                    </div>
                                    <!-- END Field: Ground Rent -->
                                </b-col>
                            </b-row>
                            <b-row>
                                <b-col
                                    cols="12"
                                    lg="6"
                                >
                                    <b-row>
                                        <b-col
                                            cols="12"
                                            lg="6"
                                        >
                                            <!-- Field: Tenure -->
                                            <div class="field-wrap">
                                                <h4 class="mb-3 color-mid-grey text-bold">
                                                    <span
                                                        v-b-tooltip.hover
                                                        title="Required"
                                                    >
                                                        Tenure
                                                        <span class="required">*</span>
                                                    </span>
                                                </h4>
                                                <label
                                                    for="field-tenure"
                                                    class="sr-only"
                                                >
                                                    Tenure
                                                </label>
                                                <div class="field-wrap__input field-wrap__input--select">
                                                    <select
                                                        id="field-tenure"
                                                        v-model="propertyForm.tenure_id"
                                                        v-validate="{ included: tenures.map(item => item.id) }"
                                                        :class="{ 'has-error': showErrors && errors.has('field-tenure') }"
                                                        name="field-tenure"
                                                        @change="saleConditionalFields"
                                                    >
                                                        <option
                                                            :value="null"
                                                            disabled
                                                            selected
                                                        >
                                                            Select a value
                                                        </option>
                                                        <option
                                                            v-for="tenure in tenures"
                                                            :key="tenure.id"
                                                            :value="tenure.id"
                                                        >
                                                            {{ tenure.name }}
                                                        </option>
                                                    </select>
                                                </div>
                                                <div
                                                    v-if="showErrors && errors.has('field-tenure')"
                                                    class="py-3"
                                                >
                                                    <div class="d-inline-block alert-error p-3 rounded-corners">
                                                        Please select a valid tenure
                                                    </div>
                                                </div>
                                            </div>
                                            <!-- END Field: Tenure -->
                                        </b-col>
                                        <b-col
                                            v-if="propertyForm.tenure_id !== 1"
                                            cols="12"
                                            lg="6"
                                        >
                                            <!-- Field: Service charge -->
                                            <h4 class="mb-3 color-mid-grey text-bold">
                                                <span
                                                    v-b-tooltip.hover
                                                    title="Required"
                                                >
                                                    Service Charge
                                                </span>
                                            </h4>
                                            <div class="field-wrap">
                                                <label
                                                    for="field-service"
                                                    class="sr-only"
                                                >
                                                    Service Charge
                                                </label>
                                                <div class="field-wrap__input field-wrap__input--prepend field-wrap__input--prepend-fill">
                                                    <input
                                                        id="field-service"
                                                        v-model="propertyForm.service_charge"
                                                        type="text"
                                                        name="field-service"
                                                        placeholder="i.e. 25 per month"
                                                    >
                                                    <div class="field-wrap__input-icon">
                                                        <SVGSprite
                                                            name="gbp"
                                                            class="form-icon"
                                                        />
                                                    </div>
                                                </div>
                                            </div>
                                            <!-- END Field: Service charge -->
                                        </b-col>
                                    </b-row>
                                </b-col>
                                <b-col
                                    v-if="propertyForm.tenure_id === 2"
                                    cols="12"
                                    lg="6"
                                >
                                    <!-- Field: Length of lease -->
                                    <div class="field-wrap">
                                        <h4 class="mb-3 color-mid-grey text-bold">
                                            <span
                                                v-b-tooltip.hover
                                                title="Required"
                                            >
                                                Length of lease
                                                <span class="required">*</span>
                                            </span>
                                        </h4>
                                        <label
                                            for="field-length"
                                            class="sr-only"
                                        >
                                            Length of lease
                                        </label>
                                        <div class="field-wrap__input">
                                            <input
                                                id="field-length"
                                                v-model="propertyForm.lease_length"
                                                v-validate="{ required: true }"
                                                :class="{ 'has-error': showErrors && errors.has('field-length') }"
                                                type="text"
                                                name="field-length"
                                                placeholder="e.g. 25 years"
                                            >
                                        </div>
                                        <div
                                            v-if="showErrors && errors.has('field-length')"
                                            class="py-3"
                                        >
                                            <div class="d-inline-block alert-error p-3 rounded-corners">
                                                Please select a valid lease length
                                            </div>
                                        </div>
                                    </div>
                                    <!-- END Field: Length of lease -->
                                </b-col>
                            </b-row>
                        </b-card-body>
                    </b-collapse>
                </div>
                <!-- END accordion 5 - Sale -->

                <!-- START accordion 6 - Let -->
                <div v-if="property.transaction_type === 'let'">
                    <b-card-header
                        header-tag="header"
                        role="tab"
                    >
                        <h2
                            v-b-toggle.accordion6
                            class="mb-0 accordion-toggle"
                        >
                            <span>Let</span>
                            <span class="d-flex align-items-center">
                                <ValidationCheck :status="validationStatus.let" />
                                <SVGSprite
                                    name="add"
                                    class="accordion-toggle__icon accordion-toggle__icon-plus"
                                />
                                <SVGSprite
                                    name="remove"
                                    class="accordion-toggle__icon accordion-toggle__icon-minus"
                                />
                            </span>
                        </h2>
                    </b-card-header>
                    <b-collapse
                        id="accordion6"
                        accordion="accordion"
                        role="tabpanel"
                    >
                        <b-card-body>
                            <b-row>
                                <b-col
                                    cols="12"
                                    lg="7"
                                    class="mb-5"
                                >
                                    <!-- Let heading -->
                                    <p>In this section you can set your letting price, deposit and other details. You can amend these details 24/7 via your online account and we are always on hand to offer guidance</p>
                                    <!-- END Let heading -->
                                </b-col>
                            </b-row>

                            <b-row>
                                <b-col
                                    cols="12"
                                    lg="6"
                                >
                                    <!-- Field: Asking Price -->
                                    <h4 class="mb-3 color-mid-grey text-bold">
                                        <span
                                            v-b-tooltip.hover
                                            title="Required"
                                        >
                                            Price
                                            <span class="required">*</span>
                                        </span>
                                    </h4>
                                    <div class="field-wrap">
                                        <label
                                            for="field-asking"
                                            class="sr-only"
                                        >
                                            Price
                                        </label>
                                        <div
                                            class="field-wrap__input field-wrap__input--prepend field-wrap__input--prepend-fill"
                                        >
                                            <input
                                                id="field-price"
                                                v-model="propertyForm.price"
                                                v-validate="{ required: true, decimal: 2, min_value: 0 }"
                                                :class="{ 'has-error': showErrors && errors.has('field-price') }"
                                                type="number"
                                                name="field-price"
                                                placeholder="0"
                                            >
                                            <div class="field-wrap__input-icon">
                                                <SVGSprite
                                                    name="gbp"
                                                    class="form-icon"
                                                />
                                            </div>
                                        </div>
                                        <div
                                            v-if="showErrors && errors.has('field-price')"
                                            class="py-3"
                                        >
                                            <div class="d-inline-block alert-error p-3 rounded-corners">
                                                Please enter a valid price
                                            </div>
                                        </div>
                                    </div>
                                    <!-- END Field: Asking Price -->
                                </b-col>
                                <b-col
                                    cols="12"
                                    lg="6"
                                >
                                    <!-- Field: Holding Price -->
                                    <h4 class="mb-3 color-mid-grey text-bold">
                                        <span
                                            v-b-tooltip.hover
                                            title="Required"
                                        >
                                            Holding Deposit
                                            <span class="required">*</span>
                                        </span>
                                    </h4>
                                    <div class="field-wrap">
                                        <label
                                            for="field-holding-deposit"
                                            class="sr-only"
                                        >
                                            Holding Deposit
                                        </label>
                                        <div class="field-wrap__input mb-4">
                                            <input
                                                id="field-holding-deposit"
                                                v-model="propertyForm.holding_deposit"
                                                v-validate="{ required: true }"
                                                type="text"
                                                name="field-holding-deposit"
                                            >
                                        </div>
                                    </div>
                                    <!-- END Field: Holding Deposit -->
                                </b-col>
                                <b-col
                                    cols="12"
                                    lg="6"
                                >
                                    <!-- Field: Asking Price -->
                                    <h4 class="mb-3 color-mid-grey text-bold">
                                        <span
                                            v-b-tooltip.hover
                                            title="Required"
                                        >
                                            Deposit
                                            <span class="required">*</span>
                                        </span>
                                    </h4>
                                    <div class="field-wrap">
                                        <label
                                            for="field-deposit"
                                            class="sr-only"
                                        >
                                            Deposit
                                        </label>
                                        <div class="field-wrap__input mb-4">
                                            <input
                                                id="field-deposit"
                                                v-model="propertyForm.deposit"
                                                v-validate="{ required: true }"
                                                :class="{ 'has-error': showErrors && errors.has('field-deposit') }"
                                                name="field-deposit"
                                            >
                                        </div>
                                        <div
                                            v-if="showErrors && errors.has('field-deposit')"
                                            class="py-3"
                                        >
                                            <div class="d-inline-block alert-error p-3 rounded-corners">
                                                Please enter a valid deposit
                                            </div>
                                        </div>
                                    </div>
                                    <!-- END Field: Asking Deposit -->
                                </b-col>
                                <b-col
                                    cols="12"
                                    lg="6"
                                >
                                    <!-- Field: Payment term -->
                                    <div class="field-wrap">
                                        <h4 class="mb-3 color-mid-grey text-bold">
                                            <span
                                                v-b-tooltip.hover
                                                title="Required"
                                            >
                                                Payment term
                                                <span class="required">*</span>
                                            </span>
                                        </h4>
                                        <label
                                            for="field-bedrooms"
                                            class="sr-only"
                                        >
                                            Payment term
                                        </label>
                                        <div class="field-wrap__input field-wrap__input--select mb-4">
                                            <select
                                                id="field-payment-term"
                                                v-model="propertyForm.payment_term"
                                                v-validate="{ required: true, included: ['per week', 'per month'] }"
                                                :class="{ 'has-error': showErrors && errors.has('field-payment-term') }"
                                                name="field-payment-term"
                                            >
                                                <option
                                                    :value="null"
                                                    disabled
                                                    selected
                                                >
                                                    Select a value
                                                </option>
                                                <option>per week</option>
                                                <option>per month</option>
                                            </select>
                                        </div>
                                        <div
                                            v-if="showErrors && errors.has('field-payment-term')"
                                            class="py-3"
                                        >
                                            <div class="d-inline-block alert-error p-3 rounded-corners">
                                                Please select a payment term
                                            </div>
                                        </div>
                                    </div>
                                    <!-- END Field: Payment term -->
                                </b-col>
                                <b-col
                                    cols="12"
                                    lg="6"
                                >
                                    <!-- Field: Furnishing -->
                                    <div class="field-wrap">
                                        <h4 class="mb-3 color-mid-grey text-bold">
                                            <span
                                                v-b-tooltip.hover
                                                title="Required"
                                            >
                                                Furnishing
                                                <span class="required">*</span>
                                            </span>
                                        </h4>
                                        <label
                                            for="field-furnishing"
                                            class="sr-only"
                                        >
                                            Furnishing
                                        </label>
                                        <div class="field-wrap__input field-wrap__input--select mb-4">
                                            <select
                                                id="field-furnishing"
                                                v-model="propertyForm.furnishing"
                                                v-validate="{ required: true, included: furnishings.map(item => item.name) }"
                                                :class="{ 'has-error': showErrors && errors.has('field-furnishing') }"
                                                name="field-furnishing"
                                            >
                                                <option
                                                    :value="null"
                                                    disabled
                                                    selected
                                                >
                                                    Select a value
                                                </option>
                                                <option
                                                    v-for="furnishing in furnishings"
                                                    :key="furnishing.id"
                                                    :value="furnishing.name"
                                                >
                                                    {{ furnishing.name }}
                                                </option>
                                            </select>
                                        </div>
                                        <div
                                            v-if="showErrors && errors.has('field-furnishing')"
                                            class="py-3"
                                        >
                                            <div class="d-inline-block alert-error p-3 rounded-corners">
                                                Please select a furnishing condition
                                            </div>
                                        </div>
                                    </div>
                                    <!-- END Field: Furnishing -->
                                </b-col>
                                <b-col
                                    cols="12"
                                    lg="6"
                                >
                                    <!-- Field: Minimum term -->
                                    <div class="field-wrap">
                                        <h4 class="mb-3 color-mid-grey text-bold">
                                            <span
                                                v-b-tooltip.hover
                                                title="Required"
                                            >
                                                Minimum term
                                                <span class="required">*</span>
                                            </span>
                                        </h4>
                                        <label
                                            for="field-minimum-term"
                                            class="sr-only"
                                        >
                                            Minimum term
                                        </label>
                                        <div class="field-wrap__input field-wrap__input--select mb-4">
                                            <select
                                                id="field-minimum-term"
                                                v-model="propertyForm.minimum_term"
                                                v-validate="{ required: true, included: minimumTerms.map(item => item.name) }"
                                                :class="{ 'has-error': showErrors && errors.has('field-minimum-term') }"
                                                name="field-minimum-term"
                                            >
                                                <option
                                                    :value="null"
                                                    disabled
                                                    selected
                                                >
                                                    Select a value
                                                </option>
                                                <option
                                                    v-for="term in minimumTerms"
                                                    :key="term.id"
                                                    :value="term.name"
                                                >
                                                    {{ term.name }}
                                                </option>
                                            </select>
                                        </div>
                                        <div
                                            v-if="showErrors && errors.has('field-minimum-term')"
                                            class="py-3"
                                        >
                                            <div class="d-inline-block alert-error p-3 rounded-corners">
                                                Please select a minimum term
                                            </div>
                                        </div>
                                    </div>
                                    <!-- END Field: Minimum term -->
                                </b-col>
                                <b-col
                                    cols="12"
                                    lg="6"
                                >
                                    <!-- Field: Change Tenancy Agreement Amount -->
                                    <div class="field-wrap">
                                        <h4 class="mb-3 color-mid-grey text-bold">
                                            <span
                                                v-b-tooltip.hover
                                                title="Required"
                                            >
                                                Change Tenancy Agreement Amount
                                                <span class="required">*</span>
                                            </span>
                                        </h4>
                                        <label
                                            for="field-minimum-term"
                                            class="sr-only"
                                        >
                                            Change Tenancy Agreement Amount
                                        </label>
                                        <div class="field-wrap__input field-wrap__input--prepend field-wrap__input--prepend-fill">
                                            <input
                                                id="field-change-agreement-amount"
                                                v-model="propertyForm.change_agreement_amount"
                                                v-validate="{ required: true, decimal: 2, min_value: 0 }"
                                                :class="{ 'has-error': showErrors && errors.has('field-change-agreement-amount') }"
                                                type="text"
                                                name="field-change-agreement-amount"
                                                placeholder="0"
                                            >
                                            <div class="field-wrap__input-icon">
                                                <SVGSprite
                                                    name="gbp"
                                                    class="form-icon"
                                                />
                                            </div>
                                        </div>
                                        <div
                                            v-if="showErrors && errors.has('field-change-agreement-amount')"
                                            class="py-3"
                                        >
                                            <div class="d-inline-block alert-error p-3 rounded-corners">
                                                Please enter a change tenancy agreement amount
                                            </div>
                                        </div>
                                    </div>
                                    <!-- END Field: Change Tenancy Agreement Amount -->
                                </b-col>
                                <b-col
                                    cols="12"
                                    lg="6"
                                >
                                    <!-- Field: Late Payment Interest Rate -->
                                    <div class="field-wrap">
                                        <h4 class="mb-3 color-mid-grey text-bold">
                                            <span
                                                v-b-tooltip.hover
                                                title="Required"
                                            >
                                                Late Rent Payment Interest Rate
                                                <span class="required">*</span>
                                            </span>
                                        </h4>
                                        <label
                                            for="field-minimum-term"
                                            class="sr-only"
                                        >
                                            Late Rent Payment Interest Rate
                                        </label>
                                        <div class="field-wrap__input field-wrap__input--append field-wrap__input--append-fill">
                                            <input
                                                id="field-late-payment-interest-rate"
                                                v-model="propertyForm.late_payment_interest_rate"
                                                v-validate="{ required: true, decimal: 2, min_value: 0 }"
                                                :class="{ 'has-error': showErrors && errors.has('field-late-payment-interest-rate') }"
                                                type="text"
                                                name="field-late-payment-interest-rate"
                                                placeholder="0"
                                            >
                                            <div class="field-wrap__input-icon text-white text-bold">
                                                %
                                            </div>
                                        </div>
                                        <div
                                            v-if="showErrors && errors.has('field-late-payment-interest-rate')"
                                            class="py-3"
                                        >
                                            <div class="d-inline-block alert-error p-3 rounded-corners">
                                                Please enter a late rent payment interest rate amount
                                            </div>
                                        </div>
                                    </div>
                                    <!-- END Field: Late Payment Interest Rate -->
                                </b-col>
                                <b-col
                                    v-if="propertyForm.country === 'Wales'"
                                    cols="12"
                                    lg="6"
                                >
                                    <!-- Field: Minimum term -->
                                    <div class="field-wrap">
                                        <h4 class="mb-3 color-mid-grey text-bold">
                                            <span
                                                v-b-tooltip.hover
                                                title="Required"
                                            >
                                                Late Rent Payment Amount
                                                <span class="required">*</span>
                                            </span>
                                        </h4>
                                        <label
                                            for="field-minimum-term"
                                            class="sr-only"
                                        >
                                            Late Rent Payment Amount
                                        </label>
                                        <div class="field-wrap__input field-wrap__input--prepend field-wrap__input--prepend-fill">
                                            <input
                                                id="field-late-payment-amount"
                                                v-model="propertyForm.late_payment_amount"
                                                v-validate="{ required: true, decimal: 2, min_value: 0 }"
                                                :class="{ 'has-error': showErrors && errors.has('field-late-payment-amount') }"
                                                type="text"
                                                name="field-late-payment-amount"
                                                placeholder="0"
                                            >
                                            <div class="field-wrap__input-icon">
                                                <SVGSprite
                                                    name="gbp"
                                                    class="form-icon"
                                                />
                                            </div>
                                        </div>
                                        <div
                                            v-if="showErrors && errors.has('field-late-payment-amount')"
                                            class="py-3"
                                        >
                                            <div class="d-inline-block alert-error p-3 rounded-corners">
                                                Please enter a late rent payment amount
                                            </div>
                                        </div>
                                    </div>
                                    <!-- END Field: Change Tenancy Agreement Amount -->
                                </b-col>
                                <b-col
                                    cols="12"
                                    lg="6"
                                >
                                    <!-- Field: Minimum term -->
                                    <div class="field-wrap">
                                        <h4 class="mb-3 color-mid-grey text-bold">
                                            <span
                                                v-b-tooltip.hover
                                                title="Required"
                                            >
                                                Lost Key / Security Device Payment Amount
                                                <span class="required">*</span>
                                            </span>
                                        </h4>
                                        <label
                                            for="field-minimum-term"
                                            class="sr-only"
                                        >
                                            Lost Key / Security Devices Amount
                                        </label>
                                        <div class="field-wrap__input field-wrap__input--prepend field-wrap__input--prepend-fill">
                                            <input
                                                id="field-lost-key-payment"
                                                v-model="propertyForm.lost_key_payment"
                                                v-validate="{ required: true, decimal: 2, min_value: 0 }"
                                                :class="{ 'has-error': showErrors && errors.has('field-lost-key-payment') }"
                                                type="text"
                                                name="field-lost-key-payment"
                                                placeholder="0"
                                            >
                                            <div class="field-wrap__input-icon">
                                                <SVGSprite
                                                    name="gbp"
                                                    class="form-icon"
                                                />
                                            </div>
                                        </div>
                                        <div
                                            v-if="showErrors && errors.has('field-lost-key-payment')"
                                            class="py-3"
                                        >
                                            <div class="d-inline-block alert-error p-3 rounded-corners">
                                                Please enter a lost key / security devices payment amount
                                            </div>
                                        </div>
                                    </div>
                                    <!-- END Field: Change Tenancy Agreement Amount -->
                                </b-col>
                            </b-row>
                        </b-card-body>
                    </b-collapse>
                </div>

                <!-- START accordion 7 - Property description -->
                <b-card-header
                    header-tag="header"
                    role="tab"
                >
                    <h2
                        v-b-toggle.accordion7
                        class="mb-0 accordion-toggle"
                    >
                        <span>Property description</span>
                        <span class="d-flex align-items-center">
                            <ValidationCheck :status="validationStatus.description" />
                            <SVGSprite
                                name="add"
                                class="accordion-toggle__icon accordion-toggle__icon-plus"
                            />
                            <SVGSprite
                                name="remove"
                                class="accordion-toggle__icon accordion-toggle__icon-minus"
                            />
                        </span>
                    </h2>
                </b-card-header>

                <b-collapse
                    id="accordion7"
                    accordion="accordion"
                    role="tabpanel"
                >
                    <b-card-body>
                        <b-row>
                            <b-col
                                cols="12"
                                lg="8"
                                class="mb-5"
                            >
                                <p>
                                    <span
                                        v-b-tooltip.hover
                                        title="Required"
                                    >
                                        The detailed description will display when a buyer or tenant clicks to view the larger and more comprehensive version of your property advert. please make your description as comprehensive and detailed as you can. Remember to include details of your local facilities, transport, links, parks and schools - where applicable. Please note that in order to comply with the terms of use of our websites and our partner portals, any personal contact details including email addresses and telephone numbers will be removed from the advert text.
                                        <span class="required">*</span>
                                    </span>
                                </p>
                                <div
                                    v-if="hasProduct('professional-property-description')"
                                >
                                    <p class="text-bold">
                                        Please note that your professional property description may take up to 48 hours to be added.
                                    </p>
                                </div>
                            </b-col>
                        </b-row>

                        <div class="field-wrap mb-0">
                            <label
                                for="field-description"
                                class="sr-only"
                            >
                                Property Description
                            </label>
                            <div class="field-wrap__input">
                                <textarea
                                    id="field-description"
                                    :key="'description'"
                                    v-model="propertyForm.description"
                                    v-validate="{ required: true }"
                                    :class="{ 'has-error': showErrors && errors.has('field-description') }"
                                    type="textarea"
                                    name="field-description"
                                    placeholder="Enter description here..."
                                    rows="10"
                                />
                            </div>
                            <div
                                v-if="showErrors && errors.has('field-description')"
                                class="py-3"
                            >
                                <div class="d-inline-block alert-error p-3 rounded-corners">
                                    Please enter a description
                                </div>
                            </div>
                        </div>

                        <div
                            v-if="!hasProduct('professional-property-description')"
                            class="mt-5 text-lg-right"
                        >
                            <button
                                type="button"
                                class="btn btn--primary"
                                @click="saveBeforeLeavingRoute = {
                                    name: 'checkout',
                                    params: {
                                        propertyId: property.id
                                    }
                                }"
                            >
                                Add Professional description
                            </button>
                        </div>
                    </b-card-body>
                </b-collapse>
                <!-- END accordion 7 - Property description -->

                <!-- START accordion 8 - Images / Video -->
                <b-card-header
                    header-tag="header"
                    role="tab"
                >
                    <h2
                        v-b-toggle.accordion8
                        class="mb-0 accordion-toggle"
                    >
                        <span>Property images / video</span>
                        <span class="d-flex align-items-center">
                            <ValidationCheck :status="validationStatus.images" />
                            <SVGSprite
                                name="add"
                                class="accordion-toggle__icon accordion-toggle__icon-plus"
                            />
                            <SVGSprite
                                name="remove"
                                class="accordion-toggle__icon accordion-toggle__icon-minus"
                            />
                        </span>
                    </h2>
                </b-card-header>

                <b-collapse
                    id="accordion8"
                    accordion="accordion"
                    role="tabpanel"
                >
                    <b-card-body>
                        <b-row>
                            <b-col
                                cols="12"
                                lg="8"
                                class="mb-5"
                            >
                                <!-- Field: Area -->
                                <div class="field-wrap mb-0">
                                    <h4 class="mb-3 color-mid-grey text-bold">
                                        Interactive Walkthough URL
                                    </h4>
                                    <label
                                        for="field-interactive-floorplan"
                                        class="sr-only"
                                    >
                                        Interactive Walkthough URL
                                    </label>
                                    <div class="field-wrap__input mb-4">
                                        <input
                                            id="field-interactive-floorplan"
                                            v-model="propertyForm.interactive_floorplan"
                                            type="text"
                                            name="field-interactive-floorplan"
                                            placeholder=""
                                        >
                                    </div>
                                </div>
                                <b-embed
                                    v-if="propertyForm.interactive_floorplan"
                                    :src="propertyForm.interactive_floorplan"
                                    type="iframe"
                                    aspect="16by9"
                                    allowfullscreen
                                />
                            </b-col>
                        </b-row>

                        <b-row>
                            <b-col
                                cols="12"
                                lg="8"
                                class="mb-5"
                            >
                                <h4 class="mb-3 color-mid-grey text-bold">
                                    Images
                                </h4>
                                <p>You can upload an image by clicking on the add image button. If you need to change the order of images, you can click on the left and right arrows to reorder them.</p>
                                <p
                                    v-if="hasProduct('professional-photography')"
                                    class="text-bold"
                                >
                                    Please note that your professional photography order may take up to 48 hours to be added. We will then either add the photographs to this area on your behalf or send them to you by email.
                                </p>
                            </b-col>
                        </b-row>

                        <div class="card card--images mb-0">
                            <div class="card__body">
                                <b-row
                                    v-if="sortedImages"
                                >
                                    <b-col
                                        v-for="image in sortedImages"
                                        :key="image.id"
                                        cols="12"
                                        md="6"
                                        lg="4"
                                        xl="3"
                                    >
                                        <div class="image-thumbnail rounded-corners mb-5">
                                            <div>
                                                <img :src="image.src">
                                                <div class="action-icons d-flex">
                                                    <button
                                                        v-if="imageCanMove(image, 'up')"
                                                        type="button"
                                                        class="action-icon"
                                                        @click="moveImage(image, 'up')"
                                                    >
                                                        <SVGSprite
                                                            name="arrow-left"
                                                        />
                                                    </button>

                                                    <button
                                                        v-if="imageCanMove(image, 'down')"
                                                        type="button"
                                                        class="action-icon"
                                                        @click="moveImage(image, 'down')"
                                                    >
                                                        <SVGSprite
                                                            name="arrow-right"
                                                        />
                                                    </button>

                                                    <button
                                                        type="button"
                                                        class="action-icon"
                                                        @click="showDeletePropertyAssetModal(image.id)"
                                                    >
                                                        <SVGSprite
                                                            name="close"
                                                        />
                                                    </button>
                                                </div>
                                            </div>
                                        </div>
                                    </b-col>
                                </b-row>

                                <button
                                    v-if="sortedImages.length < 30"
                                    class="btn btn--secondary"
                                    @click.prevent="uploadImages()"
                                >
                                    Upload Images
                                </button>
                                <div
                                    v-else
                                    class="alert alert-primary"
                                >
                                    You have reached the limit of 30 images. If you would like to add a new image, you
                                    will need to delete an existing image.
                                </div>
                            </div>
                        </div>

                        <div
                            v-if="!hasProduct('professional-photography')"
                            class="mt-5 text-lg-right"
                        >
                            <button
                                type="button"
                                class="btn btn--primary"
                                @click="saveBeforeLeavingRoute = {
                                    name: 'checkout',
                                    params: {
                                        propertyId: property.id
                                    }
                                }"
                            >
                                Add Professional photography
                            </button>
                        </div>
                    </b-card-body>
                </b-collapse>
                <!-- END accordion 8 - Images -->

                <!-- START accordion 9 - Additional documents -->
                <b-card-header
                    header-tag="header"
                    role="tab"
                >
                    <h2
                        v-b-toggle.accordion9
                        class="mb-0 accordion-toggle"
                    >
                        <span>Additional documents</span>
                        <span class="d-flex align-items-center">
                            <ValidationCheck :status="validationStatus.documents" />
                            <SVGSprite
                                name="add"
                                class="accordion-toggle__icon accordion-toggle__icon-plus"
                            />
                            <SVGSprite
                                name="remove"
                                class="accordion-toggle__icon accordion-toggle__icon-minus"
                            />
                        </span>
                    </h2>
                </b-card-header>

                <b-collapse
                    id="accordion9"
                    accordion="accordion"
                    role="tabpanel"
                >
                    <b-card-body>
                        <b-row>
                            <b-col
                                cols="12"
                                lg="8"
                                class="mb-5"
                            >
                                <p>It is a legal requirement to have {{ propertyForm.country === 'Scotland' ? 'a Home Report' : 'an Energy Performance Certificate (EPC)' }} and Gas Safety Certificate (GSC) if you are renting your property. These remain the responsibility of the landlord, but properties will be marketed without a valid {{ propertyForm.country === 'Scotland' ? 'Home Report' : 'EPC' }} and GSC.</p>
                            </b-col>
                        </b-row>

                        <b-row>
                            <b-col
                                cols="12"
                                lg="6"
                                xl="4"
                            >
                                <!-- EPC -->
                                <div class="card">
                                    <div class="card__body">
                                        <h4 class="text-bold mb-4">
                                            <span
                                                v-b-tooltip.hover
                                                title="Required"
                                            >
                                                {{ propertyForm.country === 'Scotland' ? 'Home Report' : 'EPC' }} Image
                                                <span class="required">*</span>
                                            </span>
                                        </h4>
                                        <p>Upload your {{ propertyForm.country === 'Scotland' ? 'Home Report' : 'EPC' }} image to be displayed with your property details.</p>
                                        <p
                                            v-if="!hasProduct('energy-performance-certificate-epc')"
                                            class="text-bold mb-0"
                                        >
                                            Please note that your {{ propertyForm.country === 'Scotland' ? 'Home Report' : 'Energy Performance Certificate' }} may take up to 48 hours to be added.
                                        </p>
                                        <div
                                            v-if="property.assets.epc && property.assets.epc.src"
                                            :class="!property.assets.epc.is_image ? ['file-download'] : ''"
                                            class="image-thumbnail rounded-corners mt-5"
                                        >
                                            <div class="d-flex align-items-center justify-content-center">
                                                <img
                                                    v-if="property.assets.epc.variants && property.assets.epc.variants.image_medium"
                                                    :src="property.assets.epc.variants.image_medium"
                                                >
                                                <SVGSprite
                                                    v-else
                                                    name="file"
                                                />
                                            </div>
                                        </div>
                                        <div class="mt-5">
                                            <button
                                                v-if="!property.assets.epc || !property.assets.epc.src || !hasProduct('energy-performance-certificate-epc')"
                                                type="button"
                                                class="btn btn--primary w-100 mb-3"
                                                @click="saveBeforeLeavingRoute = {
                                                    name: 'checkout',
                                                    params: {
                                                        propertyId: property.id
                                                    }
                                                }"
                                            >
                                                Purchase {{ propertyForm.country === 'Scotland' ? 'Home Report' : 'EPC' }}
                                            </button>
                                            <button
                                                class="btn btn--secondary w-100"
                                                @click.prevent="addEpc()"
                                            >
                                                Add {{ propertyForm.country === 'Scotland' ? 'Home Report' : 'EPC' }} Document
                                            </button>
                                        </div>
                                    </div>
                                </div>
                                <!-- END EPC -->
                            </b-col>
                            <b-col
                                cols="12"
                                lg="6"
                                xl="4"
                            >
                                <!-- Floorplan -->
                                <div class="card">
                                    <div class="card__body">
                                        <h4 class="text-bold mb-4">
                                            <span
                                                v-b-tooltip.hover
                                                title="Required"
                                            >
                                                Floorplan
                                                <span class="required">*</span>
                                            </span>
                                        </h4>
                                        <p>Upload your floorplan to be displayed with your property details.</p>
                                        <p
                                            v-if="!hasProduct('2d-floorplan')"
                                            class="text-bold mb-0"
                                        >
                                            Please note that your 2D Floorplan may take up to 48 hours to be added after they have been produced.
                                        </p>
                                        <div
                                            v-if="property.assets.floorplan && property.assets.floorplan.src"
                                            :class="!property.assets.floorplan.is_image ? ['file-download'] : ''"
                                            class="image-thumbnail rounded-corners mt-5"
                                        >
                                            <div class="d-flex align-items-center justify-content-center">
                                                <img
                                                    v-if="property.assets.floorplan.variants && property.assets.floorplan.variants.image_medium"
                                                    :src="property.assets.floorplan.variants.image_medium"
                                                >
                                                <SVGSprite
                                                    v-else
                                                    name="file"
                                                />
                                            </div>
                                        </div>
                                        <div class="mt-5">
                                            <button
                                                v-if="!property.assets.floorplan || !property.assets.floorplan.src || !hasProduct('2d-floorplan')"
                                                type="button"
                                                class="btn btn--primary w-100 mb-3"
                                                @click="saveBeforeLeavingRoute = {
                                                    name: 'checkout',
                                                    params: {
                                                        propertyId: property.id
                                                    }
                                                }"
                                            >
                                                Purchase 2D Floorplan
                                            </button>
                                            <button
                                                class="btn btn--secondary w-100"
                                                @click.prevent="addFloorplan()"
                                            >
                                                Add Floorplan Document
                                            </button>
                                        </div>
                                    </div>
                                </div>
                                <!-- END Floorplan -->
                            </b-col>
                            <b-col
                                v-if="property.transaction_type === 'let'"
                                cols="12"
                                lg="6"
                                xl="4"
                            >
                                <!-- GSC Image -->
                                <div class="card">
                                    <div class="card__body">
                                        <h4 class="text-bold mb-4">
                                            <span
                                                v-b-tooltip.hover
                                                title="Required"
                                            >
                                                GSC Image
                                                <span class="required">*</span>
                                            </span>
                                        </h4>
                                        <p>Upload your GSC Certificate to be displayed with your property details.</p>
                                        <div
                                            v-if="property.assets.gsc && property.assets.gsc.src"
                                            :class="!property.assets.gsc.is_image ? ['file-download'] : ''"
                                            class="image-thumbnail rounded-corners mt-5"
                                        >
                                            <div class="d-flex align-items-center justify-content-center">
                                                <img
                                                    v-if="property.assets.gsc.variants && property.assets.gsc.variants.image_medium"
                                                    :src="property.assets.gsc.variants.image_medium"
                                                >
                                                <SVGSprite
                                                    v-else
                                                    name="file"
                                                />
                                            </div>
                                        </div>
                                        <div class="mt-5">
                                            <button
                                                class="btn btn--secondary w-100"
                                                @click.prevent="addGsc()"
                                            >
                                                Add GSC Document
                                            </button>
                                        </div>
                                    </div>
                                </div>
                                <!-- END GSC Image -->
                            </b-col>
                        </b-row>
                    </b-card-body>
                </b-collapse>
                <!-- END accordion 9 - Additional documents -->

                <!-- START accordion 10 - Availability -->
                <b-card-header
                    header-tag="header"
                    role="tab"
                >
                    <h2
                        v-b-toggle.accordion10
                        class="mb-0 accordion-toggle"
                    >
                        <span>Availability</span>
                        <span class="d-flex align-items-center">
                            <ValidationCheck :status="validationStatus.availability" />
                            <SVGSprite
                                name="add"
                                class="accordion-toggle__icon accordion-toggle__icon-plus"
                            />
                            <SVGSprite
                                name="remove"
                                class="accordion-toggle__icon accordion-toggle__icon-minus"
                            />
                        </span>
                    </h2>
                </b-card-header>

                <b-collapse
                    id="accordion10"
                    accordion="accordion"
                    role="tabpanel"
                >
                    <b-card-body>
                        <b-row>
                            <b-col
                                cols="12"
                                lg="8"
                                class="mb-5"
                            >
                                <p>Click the button below to set the availability for events such as viewing etc. for this property.</p>
                            </b-col>
                        </b-row>

                        <button
                            type="button"
                            class="btn btn--secondary"
                            @click="$refs.modalAvailability.show()"
                        >
                            Edit Availability
                        </button>
                    </b-card-body>
                </b-collapse>
                <!-- END accordion 10 - Availability -->
            </div>
            <!-- END accordion -->

            <hr>

            <!-- Property actions -->
            <div class="property-actions bg-white d-flex flex-column flex-md-row align-items-center justify-content-md-between p-3 mt-5 rounded-corners">
                <button
                    class="btn btn--secondary mr-md-3"
                    @click="saveProperty()"
                >
                    Save Draft
                </button>
                <button
                    class="btn btn--primary mt-3 mt-md-0"
                    @click.prevent="submitProperty()"
                >
                    Submit for Review
                </button>
            </div>
            <!-- END Property actions -->

            <!-- START Submit for review modal -->

            <b-modal
                ref="modalPropertySubmitted"
                :hide-footer="true"
                :centered="true"
                size="lg"
                modal-class="text-center"
                title="Submitted for Approval"
            >
                <p>
                    Your property details have been submitted for approval prior to the property advert being made
                    live on Rightmove and Zoopla.
                </p>
            </b-modal>

            <!-- END Submit for review modal -->

            <!-- START View offer modal -->
            <b-modal
                ref="modalViewOffer"
                title="Offer Details"
                size="lg"
                class="property-offer-modal"
                @ok="handleUpdateOffer"
            >
                <table
                    v-if="visibleOffer"
                    class="table table-offer mb-5"
                >
                    <tbody>
                        <tr>
                            <th scope="row">
                                ID
                            </th>
                            <td>{{ visibleOffer.id }}</td>
                        </tr>
                        <tr>
                            <th scope="row">
                                Property
                            </th>
                            <td>{{ visibleOffer.property.name }}</td>
                        </tr>
                        <tr>
                            <th scope="row">
                                Status
                            </th>
                            <td>{{ visibleOffer.status.display_name }}</td>
                        </tr>
                        <tr>
                            <th scope="row">
                                Date
                            </th>
                            <td>{{ visibleOffer.created_at | moment('L') }}</td>
                        </tr>
                        <tr>
                            <th scope="row">
                                Amount
                            </th>
                            <td>{{ visibleOffer.amount | price(false) }}</td>
                        </tr>
                        <tr>
                            <th scope="row">
                                Deposit
                            </th>
                            <td>{{ visibleOffer.deposit | price(false) }}</td>
                        </tr>
                        <tr>
                            <th scope="row">
                                Name
                            </th>
                            <td>{{ visibleOffer.name }}</td>
                        </tr>
                        <tr>
                            <th scope="row">
                                Address
                            </th>
                            <td v-html="visibleOffer.address" />
                        </tr>
                        <tr>
                            <th scope="row">
                                Email Address
                            </th>
                            <td>{{ visibleOffer.email }}</td>
                        </tr>
                        <tr>
                            <th scope="row">
                                Telephone
                            </th>
                            <td>{{ visibleOffer.telephone }}</td>
                        </tr>
                        <tr>
                            <th scope="row">
                                Conditions
                            </th>
                            <td>{{ visibleOffer.conditions || '—' }}</td>
                        </tr>
                        <tr>
                            <th scope="row">
                                Buyer Position
                            </th>
                            <td>{{ visibleOffer.buyer_position }}</td>
                        </tr>
                        <tr>
                            <th scope="row">
                                Financial Position
                            </th>
                            <td>{{ visibleOffer.financial_position || '—' }}</td>
                        </tr>
                        <tr>
                            <th scope="row">
                                Mortgage Lead
                            </th>
                            <td>{{ visibleOffer.mortgage_lead || '—' }}</td>
                        </tr>
                    </tbody>
                </table>
                <div class="d-flex align-items-lg-end">
                    <router-link
                        v-if="visibleOffer"
                        :to="{ name: 'new-message-thread', params: { toType: 'offer', toId: visibleOffer.id } }"
                        class="btn btn--secondary mb-4"
                    >
                        Contact Offeree
                    </router-link>
                </div>
                <div
                    v-if="visibleOffer && visibleOffer.status && offerNeedsAction(visibleOffer.status.name)"
                    class="d-flex"
                >
                    <div class="field-wrap">
                        <div class="field-wrap__input field-wrap__input--radio">
                            <input
                                id="radio-accept-offer"
                                v-model="offerAcceptReject"
                                value="accept"
                                type="radio"
                                name="field-accept-reject"
                            >
                            <label for="radio-accept-offer"><span class="check-element check-element--invert" />Accept Offer</label>
                        </div>
                    </div>

                    <div class="field-wrap">
                        <div class="field-wrap__input field-wrap__input--radio">
                            <input
                                id="radio-reject-offer"
                                v-model="offerAcceptReject"
                                value="reject"
                                type="radio"
                                name="field-accept-reject"
                            >
                            <label for="radio-reject-offer"><span class="check-element check-element--invert" />Reject Offer</label>
                        </div>
                    </div>
                </div>
                <div
                    v-if="offerAcceptReject === 'accept'"
                    class="field-wrap field-wrap--agreement"
                >
                    <div class="field-wrap__input field-wrap__input--checkbox">
                        <input
                            id="offer-agree"
                            v-model="offerKeepPropertyActive"
                            v-validate="{ required: true }"
                            :true-value="true"
                            :false-value="false"
                            type="checkbox"
                            name="offer-agree"
                        >
                        <label
                            for="offer-agree"
                            class="p-sm"
                        >
                            <span class="check-element" />
                            <span>
                                Select this option if you would like to leave the property marketed subject to contract,
                                otherwise the property will be unlisted and removed from portals.
                            </span>
                        </label>
                    </div>
                </div>
            </b-modal>
            <!-- END View offer modal -->

            <!-- START View viewing modal -->
            <b-modal
                ref="modalViewViewing"
                title="Viewing Details"
                size="lg"
                class="property-offer-modal"
                @ok="handleUpdateViewing"
            >
                <table
                    v-if="visibleViewing"
                    class="table table-offer mb-5"
                >
                    <tbody>
                        <tr>
                            <th scope="row">
                                ID
                            </th>
                            <td>{{ visibleViewing.id }}</td>
                        </tr>
                        <tr>
                            <th scope="row">
                                Status
                            </th>
                            <td>{{ visibleViewing.status.display_name }}</td>
                        </tr>
                        <tr v-if="visibleViewing.status.name === 'approved' && visibleViewing.approved_at">
                            <th scope="row">
                                Approved at
                            </th>
                            <td>{{ visibleViewing.approved_at | moment('L') }}</td>
                        </tr>
                        <tr>
                            <th scope="row">
                                Start Time
                            </th>
                            <td>{{ visibleViewing.start_at | moment('L') }}</td>
                        </tr>
                        <tr>
                            <th scope="row">
                                Name
                            </th>
                            <td>{{ visibleViewing.user.full_name }}</td>
                        </tr>
                        <tr>
                            <th scope="row">
                                Email Address
                            </th>
                            <td>{{ visibleViewing.user.email }}</td>
                        </tr>
                        <tr>
                            <th scope="row">
                                Telephone
                            </th>
                            <td>{{ visibleViewing.user.telephone || visibleViewing.user.telephone }}</td>
                        </tr>
                        <tr>
                            <th scope="row">
                                Request Message
                            </th>
                            <td>{{ visibleViewing.request_message }}</td>
                        </tr>
                        <tr v-if="visibleViewing.status.name === 'declined'">
                            <th scope="row">
                                Reason for Declining
                            </th>
                            <td>
                                {{ visibleViewing.decline_reason }}
                            </td>
                        </tr>
                        <tr v-if="visibleViewing.status.name === 'cancelled'">
                            <th scope="row">
                                Reason for Cancellation
                            </th>
                            <td>
                                {{ visibleViewing.cancel_reason }}
                            </td>
                        </tr>
                        <tr>
                            <th scope="row">
                                Feedback
                            </th>
                            <td>{{ visibleViewing.feedback }}</td>
                        </tr>
                        <tr v-if="visibleViewing.feedback_at">
                            <th scope="row">
                                Feedback added at
                            </th>
                            <td>{{ visibleViewing.feedback_at | moment('LLLL') }}</td>
                        </tr>
                    </tbody>
                </table>
                <div class="d-flex align-items-lg-end">
                    <router-link
                        v-if="visibleViewing"
                        :to="{ name: 'new-message-thread', params: { toType: 'viewing', toId: visibleViewing.id } }"
                        class="btn btn--secondary mb-4"
                    >
                        Contact Viewer
                    </router-link>
                </div>
                <div
                    v-if="visibleViewing && visibleViewing.transaction_type === 'let' && visibleViewing.status && viewingNeedsAction(visibleViewing.status.name)"
                    class="d-flex"
                >
                    <div class="field-wrap">
                        <div class="field-wrap__input field-wrap__input--radio">
                            <input
                                id="radio-accept-viewer-tenant"
                                v-model="tenantAcceptReject"
                                value="let_agreed"
                                type="radio"
                                name="field-accept-reject-tenant"
                            >
                            <label for="radio-accept-viewer-tenant"><span class="check-element" />Accept as Tenant</label>
                        </div>
                    </div>

                    <div class="field-wrap">
                        <div class="field-wrap__input field-wrap__input--radio">
                            <input
                                id="radio-reject-viewer-tenant"
                                v-model="tenantAcceptReject"
                                value="let_declined"
                                type="radio"
                                name="field-accept-reject-tenant"
                            >
                            <label for="radio-reject-viewer-tenant"><span class="check-element" />Reject as Tenant</label>
                        </div>
                    </div>
                </div>
                <div
                    v-if="offerAcceptReject === 'accept'"
                    class="field-wrap field-wrap--agreement"
                >
                    <div class="field-wrap__input field-wrap__input--checkbox">
                        <input
                            id="offer-agree"
                            v-model="offerKeepPropertyActive"
                            v-validate="{ required: true }"
                            :true-value="true"
                            :false-value="false"
                            type="checkbox"
                            name="offer-agree"
                        >
                        <label
                            for="offer-agree"
                            class="p-sm"
                        >
                            <span class="check-element" />
                            <span>
                                Select this option if you would like to leave the property marketed subject to contract,
                                otherwise the property will be unlisted and removed from portals.
                            </span>
                        </label>
                    </div>
                </div>
            </b-modal>
            <!-- END View viewing modal -->

            <!-- START Zoopla modal -->
            <!-- TODO: Add SVG as loader -->
            <!-- TODO: Add an initial loader for when the zoopla button is clicked (need to detect when modal closed to reset) -->
            <b-modal
                id="modal-zoopla-guide-price"
                ref="modalBestGuide"
                title="Zoopla Best Guide Price"
                size="lg"
                hide-footer
            >
                <!-- Guide prices -->
                <div
                    v-if="guidePrice && guidePrice.areas.length > 0"
                    :class="{ loading : guidePriceLoader === true }"
                    class="areas"
                >
                    <div
                        v-for="area in guidePrice.areas"
                        :key="area.prices_url"
                        class="d-flex justify-content-between align-items-center area"
                    >
                        <div>
                            <span><a v-bind="{ href : area.prices_url, target : '_blank' }">{{ area.prices_url }}</a></span>
                        </div>
                        <div>
                            <span
                                v-if="area.average_sold_price_1year > 0"
                                class="d-flex flex-column align-items-end"
                            >
                                <span class="p-sm">Last year</span>
                                <span class="text-bold">{{ area.average_sold_price_1year | price(false) }}</span>
                            </span>
                            <span
                                v-else-if="area.average_sold_price_3year > 0"
                                class="d-flex flex-column align-items-end"
                            >
                                <span class="p-sm">Last 3 years</span>
                                <span class="text-bold">{{ area.average_sold_price_3year | price(false) }}</span>
                            </span>
                            <span
                                v-else-if="area.average_sold_price_5year > 0"
                                class="d-flex flex-column align-items-end"
                            >
                                <span class="p-sm">Last 5 years</span>
                                <span class="text-bold">{{ area.average_sold_price_5year | price(false) }}</span>
                            </span>
                            <span
                                v-else-if="area.average_sold_price_7year > 0"
                                class="d-flex flex-column align-items-end"
                            >
                                <span class="p-sm">Last 7 years</span>
                                <span class="text-bold">{{ area.average_sold_price_7year | price(false) }}</span>
                            </span>
                            <span v-else>
                                No recent prices
                            </span>
                        </div>
                    </div>
                </div>
                <div v-else>
                    <p>Sorry, there are no guide prices available</p>
                </div>
                <!-- END Guide prices -->

                <!-- Loader -->
                <div v-if="guidePriceLoader === true">
                    <div class="loader">
                        <SVGSprite
                            name="loader"
                            class="loader-icon loader-icon--zoopla spin-animation"
                        />
                    </div>
                </div>
                <!-- END Loader -->

                <!-- Guide price total results -->
                <div
                    v-if="guidePrice"
                    class="mt-4"
                >
                    <span class="p-sm">Total Results:&nbsp;{{ guidePrice.result_count }}</span>
                </div>
                <!-- END Guide price total results -->

                <!-- Guide price pagination -->
                <div class="d-flex justify-content-between align-items-center mt-5">
                    <div>
                        <button
                            v-if="guidePricePaging > 1"
                            class="btn btn--nomin btn--sm btn--outline"
                            @click="getPreviousPrices()"
                        >
                            Previous
                        </button>
                    </div>
                    <div>
                        <button
                            v-if="guidePrice && guidePrice.areas.length > 0"
                            class="btn btn--nomin btn--sm btn--outline"
                            @click="getNextPrices()"
                        >
                            Next
                        </button>
                    </div>
                </div>
                <!-- END Guide price pagination -->
            </b-modal>
            <!-- END Zoopla modal -->

            <b-modal
                ref="modalUnlistProperty"
                title="Unlist Property"
                ok-title="Yes"
                ok-variant="-primary"
                cancel-title="No"
                cancel-variant="-secondary"
                @ok="handleUnlistProperty"
            >
                <p>Please be aware that relisting this property in the future once it has been unlisted will incur additional charges.</p>
                <p>Are you sure you want to unlist this property?</p>
            </b-modal>

            <!-- START Relist property modal -->
            <b-modal
                ref="modalRelistProperty"
                :no-close-on-backdrop="true"
                size="lg"
                title="Relist Property"
                ok-title="Submit Relisting"
                ok-variant="-primary"
                cancel-variant="-secondary"
                @ok="handleRelistProperty"
            >
                <div class="mb-3">
                    <p>Relisting your property will allow it to be publically visible on the site for a further 6 {{ property.transaction_type === 'let' ? 'weeks' : 'months' }}.</p>
                    <p>If you would like to proceed, please review the order below, complete your details and submit to proceed.</p>
                </div>
                <div class="d-flex align-items-center justify-content-between basket-totals">
                    <div><span class="color-dark-grey">{{ property.package.name }}</span></div>
                    <div><span class="color-dark-grey">{{ property.package.taxable_recurring_price | price }}</span></div>
                </div>
                <div class="d-flex align-items-center justify-content-between basket-totals">
                    <div><span class="color-dark-grey">Subtotal (Excluding VAT)</span></div>
                    <div><span class="color-dark-grey">{{ property.package.recurring_price | price }}</span></div>
                </div>

                <div class="d-flex align-items-center justify-content-between mt-2">
                    <div><span class="color-dark-grey">VAT</span></div>
                    <div><span class="color-dark-grey">{{ property.package.taxable_recurring_price - property.package.recurring_price | price }}</span></div>
                </div>

                <div class="d-flex align-items-center justify-content-between mt-2 mb-5">
                    <div><span class="text-bold color-dark-grey">Total</span></div>
                    <div><span class="text-bold color-dark-grey">{{ property.package.taxable_recurring_price | price }}</span></div>
                </div>

                <b-col
                    cols="12"
                    class="px-0"
                >
                    <!-- Field(s): Name -->
                    <b-row>
                        <b-col
                            cols="12"
                            lg="6"
                        >
                            <!-- Field: Forename -->
                            <div class="field-wrap">
                                <h4 class="h5 mb-3 color-mid-grey text-bold">
                                    Forename
                                </h4>
                                <label
                                    for="field-renew-forename"
                                    class="sr-only"
                                >
                                    Forename
                                </label>
                                <div class="field-wrap__input field-wrap__input--border-light">
                                    <input
                                        id="field-renew-forename"
                                        v-model="relistForm.first_name"
                                        v-validate="{ required: true }"
                                        :class="{ 'has-error': showErrors && errors.has('field-renew-forename') }"
                                        type="text"
                                        name="field-forename"
                                        placeholder="Forename"
                                    >
                                </div>
                                <div
                                    v-if="showErrors && errors.has('field-renew-forename')"
                                    class="py-3"
                                >
                                    <div class="d-inline-block alert-error p-3 rounded-corners">
                                        Please enter your forename
                                    </div>
                                </div>
                            </div>
                            <!-- END Field: Forename -->
                        </b-col>
                        <b-col
                            cols="12"
                            lg="6"
                        >
                            <!-- Field: Surname -->
                            <div class="field-wrap">
                                <h4 class="h5 mb-3 color-mid-grey text-bold">
                                    Surname
                                </h4>
                                <label
                                    for="field-renew-surname"
                                    class="sr-only"
                                >
                                    Surname
                                </label>
                                <div class="field-wrap__input field-wrap__input--border-light">
                                    <input
                                        id="field-renew-surname"
                                        v-model="relistForm.last_name"
                                        v-validate="{ required: true }"
                                        :class="{ 'has-error': showErrors && errors.has('field-renew-surname') }"
                                        type="text"
                                        name="field-surname"
                                        placeholder="Surname"
                                    >
                                </div>
                                <div
                                    v-if="showErrors && errors.has('field-renew-surname')"
                                    class="py-3"
                                >
                                    <div class="d-inline-block alert-error p-3 rounded-corners">
                                        Please enter your surname
                                    </div>
                                </div>
                            </div>
                            <!-- END Field: Surname -->
                        </b-col>
                    </b-row>
                    <!-- END Field(s): Name -->

                    <!-- Field(s): Contact -->
                    <b-row>
                        <b-col
                            cols="12"
                            lg="6"
                        >
                            <!-- Field: Email -->
                            <div class="field-wrap">
                                <h4 class="h5 mb-3 color-mid-grey text-bold">
                                    Email Address
                                </h4>
                                <label
                                    for="field-renew-email"
                                    class="sr-only"
                                >
                                    Email Address
                                </label>
                                <div class="field-wrap__input field-wrap__input--border-light">
                                    <input
                                        id="field-renew-email"
                                        v-model="relistForm.email"
                                        v-validate="{
                                            required: true,
                                            email: true
                                        }"
                                        :class="{ 'has-error': showErrors && errors.has('field-renew-email') }"
                                        type="email"
                                        name="field-email"
                                        placeholder="Email Address"
                                    >
                                </div>
                                <div
                                    v-if="showErrors && errors.has('field-renew-email')"
                                    class="py-3"
                                >
                                    <div class="d-inline-block alert-error p-3 rounded-corners">
                                        Please enter a valid email address
                                    </div>
                                </div>
                            </div>
                            <!-- END Field: Email -->
                        </b-col>
                        <b-col
                            cols="12"
                            lg="6"
                        >
                            <!-- Field: Mobile telephone -->
                            <div class="field-wrap">
                                <h4 class="h5 mb-3 color-mid-grey text-bold">
                                    Telephone
                                </h4>
                                <label
                                    for="field-renew-tel"
                                    class="sr-only"
                                >
                                    Telephone
                                </label>
                                <div class="field-wrap__input field-wrap__input--border-light">
                                    <input
                                        id="field-renew-tel"
                                        v-model="relistForm.telephone"
                                        v-validate="{
                                            required: true,
                                            regex: /^\+?[0-9 ]+$/
                                        }"
                                        :class="{ 'has-error': showErrors && errors.has('field-renew-tel') }"
                                        type="tel"
                                        name="field-tel"
                                        placeholder="Telephone"
                                    >
                                </div>
                                <div
                                    v-if="showErrors && errors.has('field-renew-tel')"
                                    class="py-3"
                                >
                                    <div class="d-inline-block alert-error p-3 rounded-corners">
                                        Please enter a valid telephone number
                                    </div>
                                </div>
                            </div>
                            <!-- END Field: Mobile telephone -->
                        </b-col>
                    </b-row>
                    <!-- END Field(s): Contact -->

                    <!-- Sub heading -->
                    <h4 class="h5 mb-3 color-mid-grey text-bold">
                        Address
                    </h4>

                    <b-row
                        v-if="addresses.length"
                    >
                        <b-col
                            cols="12"
                        >
                            <div class="field-wrap__input field-wrap__input--select field-wrap__input--border-light mb-4">
                                <select
                                    id="field-renew-selectaddress"
                                    v-model="relistForm.address_id"
                                    v-validate="{ required: true }"
                                    name="field-selectaddress"
                                >
                                    <option
                                        :value="null"
                                        disabled
                                        selected
                                        hidden
                                    >
                                        Select an address
                                    </option>
                                    <option
                                        v-for="address in addresses"
                                        :key="address.id"
                                        :value="address.id"
                                    >
                                        {{ address.label }}
                                    </option>
                                    <option value="new">
                                        Add a new address
                                    </option>
                                </select>
                            </div>
                        </b-col>
                    </b-row>

                    <div v-if="relistForm.address_id === 'new'">
                        <!-- Field(s): Address -->
                        <b-row>
                            <b-col
                                cols="12"
                                lg="6"
                            >
                                <!-- Field: Search address -->
                                <div class="field-wrap">
                                    <h4 class="h5 mb-3 color-mid-grey text-bold">
                                        Address Lookup
                                    </h4>
                                    <label
                                        for="field-search"
                                        class="sr-only"
                                    >
                                        Postcode
                                    </label>
                                    <div class="field-wrap__input field-wrap__input--prepend field-wrap__input--prepend-fill field-wrap__input--border-light">
                                        <input
                                            id="field-renew-search"
                                            v-model="postcodeSearch"
                                            type="search"
                                            name="field-search"
                                            placeholder="Postcode or Location e.g. London"
                                        >
                                        <div class="field-wrap__input-icon">
                                            <SVGSprite
                                                name="search"
                                                class="form-icon"
                                            />
                                        </div>
                                    </div>
                                </div>
                                <button
                                    class="btn btn--secondary mb-5"
                                    @click.prevent="searchAddress('relist')"
                                >
                                    Search Address
                                </button>
                                <!-- END Field: Search address -->
                            </b-col>
                            <b-col
                                cols="12"
                                lg="6"
                            >
                                <!-- Field: Address Line 1 -->
                                <div class="field-wrap">
                                    <h4 class="h5 mb-3 color-mid-grey text-bold">
                                        Address
                                    </h4>
                                    <label
                                        for="field-address-1"
                                        class="sr-only"
                                    >
                                        Address Line 1
                                    </label>
                                    <div class="field-wrap__input field-wrap__input--border-light mb-4">
                                        <input
                                            id="field-renew-address-1"
                                            v-model="relistForm.address"
                                            v-validate="{ required: true }"
                                            :class="{ 'has-error': showErrors && errors.has('field-renew-address-1') }"
                                            type="text"
                                            name="field-address-1"
                                            placeholder="Address Line 1"
                                        >
                                    </div>
                                    <div
                                        v-if="showErrors && errors.has('field-renew-address-1')"
                                        class="py-3"
                                    >
                                        <div class="d-inline-block alert-error p-3 rounded-corners">
                                            Please enter your address
                                        </div>
                                    </div>
                                </div>
                                <!-- END Field: Address Line 1 -->
                            </b-col>
                        </b-row>

                        <b-row>
                            <b-col
                                cols="12"
                                lg="6"
                            >
                                <!-- Field: Address Line 2 -->
                                <div class="field-wrap">
                                    <h4 class="h5 mb-3 color-mid-grey text-bold">
                                        Address (continued)
                                    </h4>
                                    <label
                                        for="field-address-1"
                                        class="sr-only"
                                    >
                                        Address Line 2
                                    </label>
                                    <div class="field-wrap__input field-wrap__input--border-light mb-4">
                                        <input
                                            id="field-renew-address-2"
                                            v-model="relistForm.address2"
                                            :class="{ 'has-error': showErrors && errors.has('field-renew-address-2') }"
                                            type="text"
                                            name="field-address-2"
                                            placeholder="Address Line 2"
                                        >
                                    </div>
                                </div>
                                <!-- END Field: Address Line 1 -->
                            </b-col>
                            <b-col
                                cols="12"
                                lg="6"
                            >
                                <!-- Field: Town / City -->
                                <div class="field-wrap">
                                    <h4 class="h5 mb-3 color-mid-grey text-bold">
                                        Town / City
                                    </h4>
                                    <label
                                        for="field-town-city"
                                        class="sr-only"
                                    >
                                        Town / City
                                    </label>
                                    <div class="field-wrap__input field-wrap__input--border-light mb-4">
                                        <input
                                            id="field-renew-town-city"
                                            v-model="relistForm.town"
                                            v-validate="{ required: true }"
                                            :class="{ 'has-error': showErrors && errors.has('field-renew-town-city') }"
                                            type="text"
                                            name="field-town-city"
                                            placeholder="Town / City"
                                        >
                                    </div>
                                    <div
                                        v-if="showErrors && errors.has('field-renew-town-city')"
                                        class="py-3"
                                    >
                                        <div class="d-inline-block alert-error p-3 rounded-corners">
                                            Please enter your town or city
                                        </div>
                                    </div>
                                </div>
                                <!-- END Field: Town / City -->
                            </b-col>
                        </b-row>

                        <b-row>
                            <b-col
                                cols="12"
                                lg="6"
                            >
                                <!-- Field: County -->
                                <div class="field-wrap">
                                    <h4 class="h5 mb-3 color-mid-grey text-bold">
                                        County
                                    </h4>
                                    <label
                                        for="field-county"
                                        class="sr-only"
                                    >
                                        County
                                    </label>
                                    <div class="field-wrap__input field-wrap__input--border-light mb-4">
                                        <input
                                            id="field-renew-county"
                                            v-model="relistForm.county"
                                            v-validate="{ required: true }"
                                            :class="{ 'has-error': showErrors && errors.has('field-renew-county') }"
                                            type="text"
                                            name="field-county"
                                            placeholder="County"
                                        >
                                    </div>
                                    <div
                                        v-if="showErrors && errors.has('field-renew-county')"
                                        class="py-3"
                                    >
                                        <div class="d-inline-block alert-error p-3 rounded-corners">
                                            Please enter your county
                                        </div>
                                    </div>
                                </div>
                                <!-- END Field: County -->
                            </b-col>
                            <b-col
                                cols="12"
                                lg="6"
                            >
                                <!-- Field: Postcode -->
                                <div class="field-wrap">
                                    <h4 class="h5 mb-3 color-mid-grey text-bold">
                                        Postcode
                                    </h4>
                                    <label
                                        for="field-postcode"
                                        class="sr-only"
                                    >
                                        Postcode
                                    </label>
                                    <div class="field-wrap__input field-wrap__input--border-light mb-4">
                                        <input
                                            id="field-renew-postcode"
                                            v-model="relistForm.postcode"
                                            v-validate="{ required: true }"
                                            :class="{ 'has-error': showErrors && errors.has('field-renew-postcode') }"
                                            type="text"
                                            name="field-postcode"
                                            placeholder="Postcode"
                                        >
                                    </div>
                                    <div
                                        v-if="showErrors && errors.has('field-renew-postcode')"
                                        class="py-3"
                                    >
                                        <div class="d-inline-block alert-error p-3 rounded-corners">
                                            Please enter your postcode
                                        </div>
                                    </div>
                                </div>
                                <!-- END Field: Postcode -->
                            </b-col>
                        </b-row>

                        <b-row>
                            <b-col
                                cols="12"
                                lg="6"
                            >
                                <!-- Field: Country -->
                                <div class="field-wrap">
                                    <h4 class="h5 mb-3 color-mid-grey text-bold">
                                        Country
                                    </h4>
                                    <label
                                        for="field-country"
                                        class="sr-only"
                                    >
                                        Country
                                    </label>
                                    <div class="field-wrap__input field-wrap__input--select field-wrap__input--border-light mb-4">
                                        <select
                                            id="field-renew-country"
                                            v-model="relistForm.country"
                                            v-validate="{ required: true }"
                                            :class="{ 'has-error': showErrors && errors.has('field-renew-country') }"
                                            name="field-country"
                                        >
                                            <option
                                                disabled
                                                selected
                                                hidden
                                                value=""
                                            >
                                                Select a value
                                            </option>
                                            <option>England</option>
                                            <option>Wales</option>
                                            <option>Scotland</option>
                                        </select>
                                    </div>
                                    <div
                                        v-if="showErrors && errors.has('field-renew-country')"
                                        class="py-3"
                                    >
                                        <div class="d-inline-block alert-error p-3 rounded-corners">
                                            Please select your country
                                        </div>
                                    </div>
                                </div>
                                <!-- END Field: Country -->
                            </b-col>
                        </b-row>
                    </div>
                </b-col>

                <div class="d-flex align-items-center justify-content-between mt-2 basket-totals">
                    <div class="field-wrap mb-0">
                        <h4 class="h5 mb-4 color-mid-grey text-bold">
                            Card Number
                        </h4>
                        <label
                            for="field-card-number"
                            class="sr-only"
                        >
                            Card Number
                        </label>
                        <div class="field-wrap__input field-wrap__input--border-light mb-4">
                            <input
                                id="field-card-number"
                                v-model="relistForm.card.number"
                                v-validate="{ required: true, credit_card: true }"
                                :class="{ 'has-error': showErrors && errors.has('field-card-number') }"
                                type="text"
                                name="field-card-number"
                                placeholder="Card Number"
                            >
                        </div>
                        <div
                            v-if="showErrors && errors.has('field-card-number')"
                            class="py-3"
                        >
                            <div class="d-inline-block alert-error p-3 rounded-corners">
                                Please enter a valid credit/debit card number
                            </div>
                        </div>
                    </div>
                </div>

                <div class="d-flex align-items-center justify-content-between mt-2">
                    <div class="field-wrap mb-0">
                        <div class="field-wrap__input field-wrap__input--border-light mb-4">
                            <h4 class="h5 mb-4 color-mid-grey text-bold">
                                Card Owner Name
                            </h4>
                            <label
                                for="field-card-number"
                                class="sr-only"
                            >
                                Card Name
                            </label>
                            <input
                                id="field-card-name"
                                v-model="relistForm.card.name"
                                v-validate="{ required: true }"
                                :class="{ 'has-error': showErrors && errors.has('field-card-name') }"
                                type="text"
                                name="field-card-name"
                                placeholder="Card Name"
                            >
                        </div>
                        <div
                            v-if="showErrors && errors.has('field-card-name')"
                            class="py-3"
                        >
                            <div class="d-inline-block alert-error p-3 rounded-corners">
                                Please enter the card owner's name
                            </div>
                        </div>
                    </div>
                </div>

                <div class="d-flex align-items-center justify-content-between mt-2">
                    <div class="d-flex align-items-center justify-content-between flex-wrap expiry-field">
                        <h4 class="h5 mb-4 color-mid-grey text-bold w-100">
                            Expiry Date
                        </h4>
                        <label
                            for="field-card-month"
                            class="sr-only"
                        >
                            Expiry Date
                        </label>
                        <div class="field-wrap expiry-field mb-0">
                            <div class="field-wrap__input field-wrap__input--border-light mb-4">
                                <input
                                    id="field-card-month"
                                    v-model="relistForm.card.expiry_month"
                                    v-validate="{ required: true, numeric: true }"
                                    :class="{ 'has-error': showErrors && errors.has('field-card-month') }"
                                    type="text"
                                    name="field-card-month"
                                    placeholder="MM"
                                >
                            </div>
                        </div>
                        <div class="field-wrap expiry-field mb-0">
                            <div class="field-wrap__input field-wrap__input--border-light expiry-field mb-4">
                                <input
                                    id="field-card-year"
                                    v-model="relistForm.card.expiry_year"
                                    v-validate="{ required: true, numeric: true }"
                                    :class="{ 'has-error': showErrors && errors.has('field-card-year') }"
                                    type="text"
                                    name="field-card-year"
                                    placeholder="YYYY"
                                >
                            </div>
                        </div>
                        <div
                            v-if="showErrors && (errors.has('field-card-month') || errors.has('field-card-month'))"
                            class="w-100 py-3"
                        >
                            <div class="d-inline-block alert-error p-3 rounded-corners">
                                Please enter the card expiry date
                            </div>
                        </div>
                    </div>
                    <div class="cv2-field">
                        <h4 class="h5 mb-4 color-mid-grey text-bold w-100 d-flex align-items-center justify-content-between">
                            <span
                                v-b-tooltip.hover
                                title="Required field"
                            >
                                CV2
                                <span class="required">*</span>
                            </span>
                            <SVGSprite
                                v-b-tooltip.hover
                                name="info-question"
                                class="icon-tooltip"
                                title="This is the 3 digit number on the back of your card"
                            />
                        </h4>

                        <div class="field-wrap mb-0">
                            <div class="field-wrap__input field-wrap__input--border-light mb-4">
                                <input
                                    id="field-card-cv2"
                                    v-model="relistForm.card.cv2"
                                    v-validate="{ required: true, numeric: true }"
                                    :class="{ 'has-error': showErrors && errors.has('field-card-cv2') }"
                                    type="text"
                                    name="field-card-cv2"
                                    placeholder="CV2"
                                >
                            </div>
                            <div
                                v-if="showErrors && (errors.has('field-card-month') || errors.has('field-card-month'))"
                                class="w-100 py-3"
                            >
                                <div class="d-inline-block alert-error p-3 rounded-corners">
                                    Please enter the card CV2 value
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </b-modal>
            <!-- END Relist property modal -->

            <!-- START Delete property modal -->
            <b-modal
                ref="modalDeleteProperty"
                title="Delete Property"
                ok-title="Yes"
                ok-variant="-primary"
                cancel-title="No"
                cancel-variant="-secondary"
                @ok="handleDeleteProperty"
            >
                <p>Are you sure you want to request that this property be deleted?</p>
            </b-modal>
            <!-- END Delete property modal -->

            <!-- START Delete property asset modal -->
            <b-modal
                ref="modalDeletePropertyAsset"
                title="Confirm Asset Deletion"
                @ok="handleDeletePropertyAsset(deleteAssetId)"
                @cancel="deleteAssetId = null"
            >
                <p>Are you sure you want to delete this asset?</p>
            </b-modal>
            <!-- END Delete property asset modal -->

            <!-- START Upload modal -->
            <b-modal
                id="modal-upload"
                ref="modalUpload"
                :title="fileUpload.title"
                size="lg"
                hide-footer
            >
                <FileUpload
                    ref="fileUpload"
                    :label="fileUpload.label"
                    :extensions="fileUpload.extensions"
                    :mimes="fileUpload.mimes"
                    @file="handleFileSelection"
                    @upload="handleUpload"
                />
            </b-modal>
            <!-- END Upload modal -->

            <!-- START Availability modal -->
            <b-modal
                ref="modalAvailability"
                title="Edit Availability"
                size="lg"
                ok-title="Save"
                hide-header-close
                no-close-on-backdrop
                no-close-on-esc
                @ok="handleAvailabilityUpdate"
            >
                <EditAvailability
                    :property="property"
                    @updated="data => eventSlots = data"
                />
            </b-modal>
            <!-- END Availability modal -->

            <!-- START Save before leaving modal -->
            <b-modal
                ref="modalSaveBeforeLeaving"
                :visible="!!saveBeforeLeavingRoute"
                title="Save your changes?"
                ok-title="Save Changes"
                ok-variant="-secondary"
                cancel-title="Discard Changes"
                cancel-variant="-primary"
                hide-header-close
                no-close-on-backdrop
                no-close-on-esc
                @ok="handleSaveBeforeLeaving()"
                @cancel="handleSaveBeforeLeaving(false)"
            >
                <p>Do you want to save your changes before leaving?</p>
            </b-modal>
            <!-- END Save before leaving modal -->

            <!-- START Property lookup modal -->
            <PostcodeLookup
                ref="postcodeLookup"
                @propertySelected="prefillAddress"
                @pageLoaderRemove="hidePageLoader"
            />
            <!-- END Property lookup modal -->

            <transition name="fade">
                <div
                    v-if="pageLoader"
                    class="page-loader"
                >
                    <div>
                        <SVGSprite
                            name="loader"
                            class="loader-icon spin-animation"
                        />
                    </div>
                </div>
            </transition>
        </div>
        <Forbidden
            v-else-if="showError"
            :error="error"
        />
    </div>
</template>


<script>
import { mapActions, mapGetters, mapState } from 'vuex';
import axios from 'axios';
import { cloneDeep, omit, pick, uniqueId } from 'lodash-es';
import AMLCheck from '../components/AMLCheck';
import OwnersRepeater from '../components/OwnersRepeater';
import PostcodeLookup from '../components/PostcodeLookup';
import FileUpload from '../components/FileUpload';
import EditAvailability from '../components/EditAvailability';
import ValidationCheck from '../components/ValidationCheck';

export default {
    name: 'Property',
    components: {
        AMLCheck,
        OwnersRepeater,
        PostcodeLookup,
        FileUpload,
        EditAvailability,
        ValidationCheck
    },
    data() {
        return {
            fileUpload: {
                file: null,
                title: 'Upload File',
                label: 'Select file to upload',
                extensions: ['jpg', 'jpeg', 'pdf', 'png'],
                mimes: ['application/pdf', 'image/jpeg', 'image/png'],
                type: ''
            },
            owners: [],
            guidePostcode: null,
            guidePrice: null,
            guidePriceLoader: false,
            guidePricePaging: 1,
            pageLoader: false,
            postcodeSearch: '',
            postcodeSearchTarget: 'property',
            propertyForm: {
                address: null,
                address2: null,
                area: null,
                bathrooms: null,
                bedrooms: null,
                change_agreement_amount: null,
                country: null,
                county: null,
                date_available: null,
                deleted_at: null,
                deposit: null,
                description: null,
                expires_at: null,
                first_name: null,
                floors: null,
                furnishing: null,
                garages: null,
                ground_rent: null,
                has_epc: null,
                holding_deposit: null,
                house_name_number: null,
                interactive_floorplan: null,
                last_name: null,
                late_payment_interest_rate: null,
                late_payment_amount: null,
                lease_length: null,
                lost_key_payment: null,
                minimum_term: null,
                mobile_telephone: null,
                package: null,
                package_id: null,
                payment_term: null,
                postcode: null,
                price: null,
                price_prefix: null,
                property_type_id: null,
                receptions: null,
                service_charge: null,
                should_expire: null,
                sold_at: null,
                summary: null,
                telephone: null,
                tenure_id: null,
                town: null,
                user_ownership: null,
                virtual_tour: null
            },
            relistForm: {
                first_name: '',
                last_name: '',
                email: '',
                telephone: '',
                address_id: null,
                address: '',
                address2: '',
                town: '',
                county: '',
                postcode: '',
                country: '',
                card: {
                    number: '',
                    name: '',
                    expiry_month: '',
                    expiry_year: '',
                    cv2: ''
                }
            },
            features: [],
            eventSlots: [],
            galleryImages: [],
            deleteAssetId: null,
            showErrors: false,
            visibleOffer: null,
            visibleViewing: null,
            offerAcceptReject: null,
            tenantAcceptReject: null,
            offerKeepPropertyActive: true,
            notificationsCheck: true,
            validationGroups: {
                details: [
                    'field-house-name-number',
                    'field-address-1',
                    'field-address-2',
                    'field-town-city',
                    'field-county',
                    'field-postcode',
                    'field-country',
                    'field-type',
                    'field-date'
                ],
                featurePoints: [
                    'field-bedrooms',
                    'field-bathrooms',
                    'field-floors',
                    'field-reception',
                    'field-garages',
                    'field-area'
                ],
                sale: [
                    'field-qualifier',
                    'field-tenure',
                    'field-asking',
                    'field-service'
                ],
                let: [
                    'field-price',
                    'field-holding-deposit',
                    'field-deposit',
                    'field-payment-term',
                    'field-minimum-term',
                    'field-furnishing',
                    'field-change-agreement-amount',
                    'field-late-payment-interest-rate',
                    'field-late-payment-amount',
                    'field-lost-key-payment'
                ],
                description: [
                    'field-description'
                ]
            },
            saveBeforeLeavingRoute: null,
            error: null
        };
    },
    computed: {
        ...mapState('user', ['user']),
        ...mapState('properties', ['property', 'notifications']),
        ...mapState('global', ['products', 'propertyTypes', 'tenures', 'qualifiers', 'minimumTerms', 'furnishings']),
        ...mapGetters('properties', ['notifications']),
        ...mapGetters('user', ['addresses']),
        propertyProducts() {
            const products = [...new Set([...this.property.package.products, ...this.property.products])];

            return products.map(id => this.products.find(product => product.id === id)).filter(product => product && product.id);
        },
        offersPrevButtonActive() {
            return this.property.offers.current_page > 1;
        },
        offersNextButtonActive() {
            return this.property.offers.current_page > 0 && this.property.offers.current_page < this.property.offers.last_page;
        },
        sortedOwners() {
            return {
                remove: this.owners.filter(owner => owner.markedForDelete).map(owner => owner.id),
                existing: this.owners.filter(owner => owner.existing && !owner.markedForDelete).map(owner => owner.id),
                new: this.owners.filter(owner => !owner.existing && !owner.markedForDelete)
            };
        },
        selectableOwners() {
            return this.user.owners;
        },
        isNew() {
            return this.property && this.property.status.name === 'draft';
        },
        validationStatus() {
            const groups = {};
            groups.aml = {
                value: this.user && this.user.aml_requested
            };
            Object.keys(this.validationGroups).forEach((key) => {
                groups[key] = {
                    value: !this.validationGroups[key].filter(field => this.fields && this.fields[field] && !this.fields[field].valid).length
                };
            });
            groups.ownership = {
                value: ((this.propertyForm.user_ownership === false || this.propertyForm.user_ownership === 'with_others') && this.owners.length > 0) || this.propertyForm.user_ownership === true
            };
            groups.featurePoints = {
                value: groups.featurePoints.value && this.features.filter(feature => feature.content).length >= 3
            };
            groups.images = {
                value: this.property.assets.gallery_image && this.property.assets.gallery_image.length > 0,
                optional: true
            };
            groups.documents = {
                value: !!(this.property.assets.epc && this.property.assets.floorplan && (this.property.transaction_type === 'sale' || this.property.assets.gsc)),
                optional: true
            };
            groups.availability = {
                value: this.property.availabilities && this.property.availabilities.length > 0
            };
            groups.pass = Object.keys(groups).every(key => groups[key].optional || !!groups[key].value);
            return groups;
        },
        sortedFeatures() {
            return this.features.slice().sort((a, b) => a.order - b.order);
        },
        sortedImages() {
            try {
                return this.galleryImages.slice().sort((a, b) => a.order - b.order);
            }
            catch (e) {
                return [];
            }
        },
        canBeRelisted() {
            return ['expired', 'withdrawn'].indexOf(this.property.status.name) !== -1;
        },
        hasAllProducts() {
            try {
                return this.property.products.length >= this.products.length;
            }
            catch (e) {
                return false;
            }
        },
        viewings() {
            try {
                return this.property.events.filter(event => event.type === 'viewing');
            }
            catch (e) {
                return [];
            }
        },
        dateAvailable() {
            try {
                const date = this.$moment(this.propertyForm.date_available);
                return date.isValid() ? date.format('YYYY-MM-DD') : null;
            }
            catch (e) {
                return null;
            }
        },
        showError() {
            return Number(parseFloat(this.error)) === this.error;
        }
    },
    mounted() {
        // Get properties
        this.getProperty({ propertyId: this.$route.params.id, edit: true })
            .then((result) => {
                if (result === true) {
                    this.error = false;
                    this.populateForm();
                    if (this.property.transaction_type === 'sale') {
                        this.saleConditionalFields();
                    }
                }
                else {
                    this.error = result;
                }
            });
    },
    methods: {
        ...mapActions('user', ['refreshUser']),
        ...mapActions('properties', ['getProperty', 'updateProperty', 'deleteProperty', 'unlistProperty', 'uploadPropertyAsset', 'deletePropertyAsset', 'reorderImages', 'updateAvailability', 'submitForReview', 'getOffers', 'updateOffer', 'updateViewing']),
        ...mapActions('notifications', {
            markNotificationsAsRead: 'markAsRead'
        }),
        ...mapActions('checkout', ['relistProperty']),
        addGsc() {
            this.fileUpload.type = 'gsc';
            this.fileUpload.title = 'Upload GSC';
            this.setUploadTypes('document');
            this.$refs.modalUpload.show();
        },
        addEpc() {
            this.fileUpload.type = 'epc';
            this.fileUpload.title = 'Upload EPC Image';
            this.setUploadTypes('document');
            this.$refs.modalUpload.show();
        },
        addFloorplan() {
            this.fileUpload.type = 'floorplan';
            this.fileUpload.title = 'Upload Floorplan';
            this.setUploadTypes('document');
            this.$refs.modalUpload.show();
        },
        uploadImages() {
            try {
                if (this.sortedImages.length < 30) {
                    this.fileUpload.type = 'gallery_image';
                    this.fileUpload.title = 'Upload Image';
                    this.setUploadTypes();
                    this.$refs.modalUpload.show();
                    return true;
                }
                throw new Error();
            }
            catch (e) {
                return false;
            }
        },
        submitProperty() {
            return this.$validator.validateAll().then((validationResult) => {
                if (validationResult && this.validationStatus.pass) {
                    return this.saveProperty()
                        .then((saveResult) => {
                            if (saveResult) {
                                if (!this.user.aml_requested) {
                                    this.$toasted.error('You need to have requested or already passed an AML check before you can submit your property for review.');
                                    return false;
                                }

                                return this.submitForReview(this.property.id)
                                    .then(() => {
                                        this.getProperty({ propertyId: this.property.id, edit: true, clearExisting: false })
                                            .then(() => {
                                                this.populateForm();
                                            });
                                        this.$refs.modalPropertySubmitted.show();
                                    })
                                    .catch(() => {
                                        this.$toasted.error('An error occured while attempting to submit your property for review.');
                                    });
                            }
                            return false;
                        });
                }

                this.showErrors = true;
                this.$toasted.error('Your property information has errors. Please correct them and try again.');

                return Promise.resolve(false);
            });
        },
        saveProperty() {
            return this.updateProperty({
                id: this.property.id,
                ...this.propertyForm,
                date_available: this.dateAvailable,
                user_ownership: this.propertyForm.user_ownership !== false,
                owners: this.sortedOwners,
                features: this.sortedFeatures.map(feature => feature.content)
            })
                .then(() => {
                    this.refreshUser();
                    this.getProperty({ propertyId: this.property.id, edit: true, clearExisting: false })
                        .then(() => {
                            this.populateForm();
                        });
                    this.$toasted.success('Your property details have been saved.');
                    return true;
                })
                .catch(() => {
                    this.$toasted.error('There was an error while attempting to update the property.');
                    return false;
                });
        },
        addOwner() {
            this.owners.push({
                key: uniqueId(),
                existing: false,
                markedForDelete: false
            });
        },
        getGuidePrice() {
            this.showPricesLoader(); // show loader as soon as function ran

            const errorAlert = 'Sorry, we could not find results for your search parameter, make sure to enter a valid postcode.'; // Error text

            // HTTP request for zoopla api
            axios
                .get(`properties/zoopla?postcode=${this.guidePostcode}&page=${this.guidePricePaging}`)
                .then((response) => {
                    this.guidePrice = response.data.payload.prices; // Add API returned data to vue
                    this.$refs.modalBestGuide.show(); // Show modal
                    this.hidePricesLoader(); // hide loader
                })
                .catch((error) => {
                    this.hidePricesLoader(); // hide loader
                    this.$toasted.error(errorAlert);
                });
        },
        showPricesLoader() {
            this.guidePriceLoader = true;
        },
        hidePricesLoader() {
            this.guidePriceLoader = false;
            this.hidePageLoader();
        },
        getInitialPrices() {
            this.guidePricePaging = 1;
            this.getGuidePrice();
            this.showPageLoader();
        },
        getPreviousPrices() {
            this.guidePricePaging--;
            this.getGuidePrice();
        },
        getNextPrices() {
            this.guidePricePaging++;
            this.getGuidePrice();
        },
        showPageLoader() {
            this.pageLoader = true;
        },
        hidePageLoader() {
            this.pageLoader = false;
        },
        searchAddress(target = 'property') {
            this.postcodeSearchTarget = target;
            this.showPageLoader();
            this.$refs.postcodeLookup.findAddress(this.postcodeSearch);
        },
        prefillAddress(property) {
            // Format town as it seems to come as all capitals
            let town = property.post_town;
            town = town.toLowerCase();
            town = town.charAt(0).toUpperCase() + town.substr(1);

            const addressData = this.postcodeSearchTarget === 'relist' ? this.relistForm : this.propertyForm;

            // Set our address field as the results of what was selected
            const houseNumberName = property.building_number || property.building_name;

            if (houseNumberName) {
                addressData.house_name_number = houseNumberName;
                addressData.address = property.thoroughfare;
                addressData.address2 = property.line_2 !== houseNumberName ? property.line_2 : property.line_3;
            }
            else {
                addressData.house_name_number = property.line_1;
                addressData.address = property.line_2;
                addressData.address2 = property.line_3;
            }

            // Set our address field as the results of what was selected
            addressData.postcode = property.postcode;
            addressData.town = town;
            addressData.county = property.county;
            addressData.country = property.country;
        },
        resetFileUpload() {
            this.fileUpload.file = null;
            this.title = 'Upload File';
            this.label = 'Select file to upload';
            this.extensions = [];
            this.mimes = [];
            this.$refs.fileUpload.reset();
        },
        handleRelistProperty(bvEvt) {
            bvEvt.preventDefault();

            const address = this.relistForm.address_id === 'new'
                ? pick(this.relistForm, 'address_id', 'address', 'address2', 'postcode', 'town', 'county', 'postcode', 'country')
                : { address_id: this.relistForm.address_id };

            this.relistProperty({
                order_type: 'relist',
                ...omit(this.relistForm, 'address_id', 'address', 'address2', 'postcode', 'town', 'county', 'postcode', 'country'),
                ...address,
                property_id: this.property.id,
                package_id: this.property.package.id,
                subtotal: this.property.package.recurring_price,
                tax: (this.property.package.taxable_recurring_price - this.property.package.recurring_price),
                total: this.property.package.taxable_recurring_price
            })
                .then(() => {
                    this.refreshUser();
                    this.getProperty({ propertyId: this.property.id, edit: true, clearExisting: false })
                        .then(() => {
                            this.populateForm();
                        });
                    this.$toasted.success('Your property relisting has been successful.');
                    this.$refs.modalRelistProperty.hide();
                })
                .catch(() => {
                    this.$toasted.error('There was an error while attempting to process your relisting request.');
                });
        },
        handleDeleteProperty() {
            this.deleteProperty(this.property.id)
                .then(() => {
                    this.$toasted.success('Your request to delete this property has been successfully made.');
                })
                .catch(() => {
                    this.$toasted.error('An error occured while attempting to delete your property.');
                });
        },
        handleFileSelection({ file, type }) {
            if (file) {
                this.fileUpload.file = file;
            }
            else {
                this.fileUpload.file = null;
                this.fileUpload.type = '';
            }
        },
        handleUpload() {
            this.uploadPropertyAsset({
                propertyId: this.property.id,
                type: this.fileUpload.type,
                file: this.fileUpload.file,
                name: this.fileUpload.name
            })
                .then(() => {
                    this.getProperty({ propertyId: this.$route.params.id, edit: true, clearExisting: false })
                        .then(() => {
                            this.populateImages();
                        });
                    this.$toasted.success('Your upload has been completed.');
                    this.$refs.modalUpload.hide();
                    this.resetFileUpload();
                })
                .catch((e) => {
                    try {
                        if (e.response.data.errors.file) {
                            this.$toasted.error(e.response.data.errors.file);
                        }
                    }
                    catch (err) {
                        this.$toasted.error('There was an error while attempting to upload the file.');
                    }
                    this.$refs.modalUpload.hide();
                    this.resetFileUpload();
                });
        },
        populateForm() {
            Object.assign(this.propertyForm, cloneDeep(pick(this.property, [
                'address',
                'address2',
                'area',
                'bathrooms',
                'bedrooms',
                'change_agreement_amount',
                'country',
                'county',
                'date_available',
                'deposit',
                'description',
                'epc_received',
                'epc_status',
                'first_name',
                'floors',
                'furnishing',
                'garages',
                'ground_rent',
                'has_epc',
                'holding_deposit',
                'house_name_number',
                'interactive_floorplan',
                'late_payment_interest_rate',
                'late_payment_amount',
                'last_name',
                'lease_length',
                'lost_key_payment',
                'minimum_term',
                'mobile_telephone',
                'payment_term',
                'postcode',
                'price',
                'price_prefix',
                'property_type_id',
                'receptions',
                'send_text',
                'service_charge',
                'should_expire',
                'summary',
                'telephone',
                'tenure_id',
                'town',
                'transaction_type',
                'under_offer_until',
                'user_ownership',
                'viewing_availability',
                'virtual_tour',
                'withdrawn'
            ])));

            if (this.property.owners && this.property.owners.length) {
                if (this.property.user_ownership === true) {
                    this.propertyForm.user_ownership = 'with_others';
                }
                this.owners = cloneDeep(this.property.owners.map(owner => ({ key: uniqueId(), ...owner })));
            }
            else {
                this.owners = [];
            }

            if (this.property.features && this.property.features.length) {
                this.features = cloneDeep(this.property.features.map((feature, index) => ({
                    key: uniqueId(),
                    content: feature,
                    order: (index + 1)
                })));
                if (this.property.features.length < 3) {
                    this.featureAdd((3 - this.property.features.length));
                }
            }
            else {
                this.features = [];
                this.featureAdd(3);
            }

            this.populateImages();
        },
        populateImages() {
            try {
                this.galleryImages = this.property.assets.gallery_image.map((asset, index) => ({
                    ...asset,
                    order: (index + 1)
                }));
            }
            catch (e) {
                this.galleryImages = [];
            }
        },
        handleOwnerRemove(id, index) {
            const removedOwner = id ? this.owners.find(owner => owner.property_owner_id === id) : false;
            if (removedOwner && removedOwner.existing) {
                removedOwner.markedForDelete = !removedOwner.markedForDelete;
            }
            else {
                this.owners.splice(index, 1);
            }
        },
        handleAvailabilityUpdate() {
            this.updateAvailability({
                propertyId: this.property.id,
                slots: this.eventSlots
            })
                .then(() => {
                    this.getProperty({ propertyId: this.property.id, edit: true, clearExisting: false });
                    this.$toasted.success('The viewing availability for this property has been successfully updated');
                })
                .catch(() => {
                    this.$toasted.error('An error occured while attempting to update the viewing availability for this property');
                });
        },
        handleDeletePropertyAsset(id) {
            this.deletePropertyAsset({ propertyId: this.property.id, id })
                .then(() => {
                    this.getProperty({ propertyId: this.property.id, edit: true, clearExisting: false })
                        .then(() => {
                            this.populateImages();
                        });
                    this.$toasted.success('Image successfully deleted');
                })
                .catch(() => {
                    this.$toasted.error('An error occured while attempting to delete the image');
                });
            this.deleteAssetId = null;
        },
        showOffer(id) {
            this.visibleOffer = this.property.offers.find(order => order.id === id);
            this.$refs.modalViewOffer.show();
        },
        showViewing(id) {
            this.visibleViewing = this.viewings.find(viewing => viewing.id === id);
            this.$refs.modalViewViewing.show();
        },
        hasProduct(slug) {
            const product = this.products.find(item => item.slug === slug);
            return product && (this.property.package.products.indexOf(product.id) !== -1 || this.property.products.indexOf(product.id) !== -1);
        },
        offerNeedsAction(status) {
            return ['accepted', 'rejected', 'withdrawn'].indexOf(status) === -1;
        },
        viewingNeedsAction(status) {
            return ['declined', 'cancelled', 'let_agreed'].indexOf(status) === -1;
        },
        handleUpdateOffer(bvEvt) {
            if (this.visibleOffer && this.offerAcceptReject !== null) {
                bvEvt.preventDefault();
                if (this.offerAcceptReject !== 'accept' || this.visibleOffer.aml_passed) {
                    this.updateOffer({
                        propertyId: this.property.id,
                        offerId: this.visibleOffer.id,
                        action: this.offerAcceptReject,
                        unlist_property: !this.offerKeepPropertyActive
                    })
                        .then(() => {
                            this.getProperty({ propertyId: this.property.id, edit: true, clearExisting: false });
                            this.$refs.modalViewOffer.hide();
                            this.$toasted.success(this.offerAcceptReject === 'accept' ? 'You have successfully accepted the offer.' : 'You have successfully rejected the offer.');
                        })
                        .catch(() => {
                            this.$toasted.error('An error occured while attempting to update the offer status.');
                        });
                }
                else {
                    this.$toasted.error('This offer cannot be accepted at the moment as the offeree has not passed an AML check.');
                }
            }
        },
        handleUpdateViewing(bvEvt) {
            if (this.visibleViewing && this.tenantAcceptReject !== null) {
                bvEvt.preventDefault();
                this.updateViewing({
                    eventId: this.visibleViewing.id,
                    action: this.tenantAcceptReject
                })
                    .then(() => {
                        this.getProperty({ propertyId: this.property.id, edit: true, clearExisting: false });
                        this.$refs.modalViewViewing.hide();
                        this.$toasted.success(this.tenantAcceptReject === 'accept' ? 'You have successfully accepted the viewer as the tenant.' : 'You have successfully rejected the viewer as tenant.');
                    })
                    .catch(() => {
                        this.$toasted.error('An error occured while attempting to update the viewing status.');
                    });
            }
        },
        saleConditionalFields() {
            const leaseholdTenure = this.tenures.find(tenure => tenure.name.toLowerCase() === 'leasehold');
            let groundRentIndex = this.validationGroups.sale.indexOf('field-ground');
            let leaseLengthIndex = this.validationGroups.sale.indexOf('field-length');

            if (leaseholdTenure && this.propertyForm.tenure_id === leaseholdTenure.id) {
                if (groundRentIndex === -1) {
                    this.validationGroups.sale.push('field-ground');
                }

                if (leaseLengthIndex === -1) {
                    this.validationGroups.sale.push('field-length');
                }
            }
            else {
                groundRentIndex = this.validationGroups.sale.indexOf('field-ground');
                if (groundRentIndex !== -1) {
                    this.validationGroups.sale.splice(groundRentIndex, 1);
                }

                leaseLengthIndex = this.validationGroups.sale.indexOf('field-length');
                if (leaseLengthIndex !== -1) {
                    this.validationGroups.sale.splice(leaseLengthIndex, 1);
                }
            }
        },
        featureAdd(number = 1) {
            if (number) {
                const features = [];
                for (let i = 1; i <= number; i++) {
                    features.push({
                        key: uniqueId(),
                        content: '',
                        order: (this.features.length + 1)
                    });
                }
                this.features.push(...features);
            }
        },
        featureMoveUp(feature) {
            const above = this.sortedFeatures.find(item => item.order === (feature.order - 1));
            if (above) {
                ++above.order;
            }
            if (feature.order > 1) {
                --feature.order;
            }
        },
        featureMoveDown(feature) {
            const below = this.sortedFeatures.find(item => item.order === (feature.order + 1));
            if (below) {
                --below.order;
            }
            if (feature.order <= this.features.length) {
                ++feature.order;
            }
        },
        featureRemove(feature) {
            this.features.splice(this.features.findIndex(item => item.key === feature.key), 1);
            this.features = this.features.map((item, index) => ({
                ...item,
                order: (index + 1)
            }));
        },
        showDeletePropertyAssetModal(id) {
            this.deleteAssetId = id;
            this.$refs.modalDeletePropertyAsset.show();
        },
        imageCanMove(asset, direction) {
            const order = direction === 'up' ? (asset.order - 1) : (asset.order + 1);
            return this.galleryImages.find(item => item.order === order);
        },
        moveImage(asset, direction) {
            if (direction === 'up') {
                const above = this.galleryImages.find(item => item.order === (asset.order - 1));
                if (above) {
                    ++above.order;
                }
                if (asset.order > 1) {
                    --asset.order;
                }
            }
            else if (direction === 'down') {
                const below = this.galleryImages.find(item => item.order === (asset.order + 1));
                if (below) {
                    --below.order;
                }
                if (asset.order <= this.galleryImages.length) {
                    ++asset.order;
                }
            }

            this.reorderImages({
                propertyId: this.property.id,
                assets: this.sortedImages
            })
                .then(() => {
                    this.getProperty({ propertyId: this.$route.params.id, edit: true, clearExisting: false })
                        .then(() => {
                            this.populateImages();
                        });
                })
                .catch(() => {
                    this.$toasted.error('An error occured while attempting to update the assets');
                });
        },
        handleUnlistProperty() {
            this.unlistProperty({ propertyId: this.property.id })
                .then(() => {
                    this.getProperty({ propertyId: this.$route.params.id, edit: true, clearExisting: false });
                    this.$toasted.success('Your property has been successfully unlisted.');
                });
        },
        handleSaveBeforeLeaving(save = true) {
            const route = this.saveBeforeLeavingRoute;
            const next = () => {
                this.$nextTick(() => {
                    this.$router.push(route);
                });
            };

            if (route && save) {
                this.saveProperty().then(next);
            }
            else if (route) {
                next();
            }
            else {
                this.$toasted.error('An unknown error occured, please try again or contact us if this error persists');
            }

            this.saveBeforeLeavingRoute = null;
        },
        setUploadTypes(category) {
            const mimes = ['image/jpeg', 'image/png'];
            const extensions = ['.jpg', '.jpeg', '.png'];

            if (category === 'document') {
                mimes.push(
                    'application/msword',
                    'application/pdf',
                    'application/rtf',
                    'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
                    'application/vnd.openxmlformats-officedocument.wordprocessingml.template'
                );
                extensions.push('.doc', '.docx', '.dot', '.dotx', '.rtf', '.pdf');
            }

            this.fileUpload.mimes = mimes;
            this.fileUpload.extensions = extensions;
        }
    }
};
</script>

<style lang="scss">

// Main package title
.package-icon {
    width: 4rem;
    margin-right: 2rem;
}

// Listing with icons
.icon-list {
    display: flex;
    flex-wrap: wrap;

    li {
        margin-bottom: 1rem;
        flex: 0 1 100%;

        &:last-child {
            margin-bottom: 0;
        }
    }

    &--split {
        margin-left: -10px;
        margin-right: -10px;

        li {
            padding-left: 10px;
            padding-right: 10px;

            @include media-breakpoint-up(lg) {
                flex: 0 1 50%;
            }
        }
    }

    &__svg {
        width: 2rem;
        margin-right: 2rem;
        color: theme-color('secondary');
    }

    &--alert {
        .icon-list__svg {
            color: theme-color('primary');
        }
    }
}

// aml check modal
.modal {
    &#modal-zoopla-guide-price {
        .modal-dialog {
            .modal-header {
                background: #5D1F62;
            }
        }
    }
}

// Zoopla button
.btn.zoopla-button,
.btn.zoopla-button:hover,
.btn.zoopla-button:focus,
.btn.zoopla-button:active {
    background: transparent !important;
    box-shadow: none;
    border: 0 !important;
    min-width: 0;
    position: absolute;
    top: 50%;
    right: 1rem;
    transform: translateY(-50%);
    outline: none;
    width: auto;
}

// Guide price modal
.areas {
    transition: opacity .3s ease;

    &.loading {
        opacity: .2;
    }

    .area {
        border-bottom: 1px solid theme-color('light-grey');
        padding: .5rem 0;
        min-height: 52px;
        color: theme-color('dark-grey');

        a {
            font-size: 12px;
            color: #5D1F62;
        }

        &:first-of-type {
            padding-top: 0;
        }

        &:last-of-type {
            padding-bottom: 0;
            border-bottom: 0;
        }
    }
}

// alerts card
.alerts-card {
    @include media-breakpoint-down(md) {
        .table-flex > div > div:first-of-type {
            border-top: 1px solid theme-color('light-grey');
        }
    }
}

@include media-breakpoint-down(sm) {
    .page-property .btn:not(.btn--sm):not(.zoopla-button) {
        width: 100%;
        display: block;
    }
}

.zoopla-icon {
    color: #5D1F62;
    width: 8rem;
}

.card--images {
    .col-12 {
        &:first-of-type {
            .image-thumbnail {
                border: 5px solid theme-color('primary');

                &::before {
                    content: "Featured Image";
                    position: absolute;
                    top: -23px;
                    left: 0;
                    color: theme-color('primary');
                    font-size: 10px;
                }
            }
        }
    }
}

.image-thumbnail {
    .action-icons {
        position: absolute;
        top: 1rem;
        right: 1rem;
    }

    .action-icon {
        width: 3rem;
        height: 3rem;
        background-color: theme-color('primary');
        color: #ffffff;
        margin: 0;
        padding: 0.75rem;
        border-radius: 100%;
        visibility: hidden;
        opacity: 0;
        transition-property: opacity, visibility, background-color;
        transition-duration: 0.1s;
        transition-timing-function: ease-in-out;

        .svg-sprite {
            display: flex;
        }

        svg {
            width: 1.5rem;
            height: 1.5rem;
        }

        @include hover() {
            background-color: darken(theme-color('primary'), 15%);
        }

        + .action-icon {
            margin-left: 0.75rem;
        }
    }

    @include hover() {
        .action-icon {
            visibility: visible;
            opacity: 1;
        }
    }
}

.table-offer {
    th {
        width: 25%;
    }
}

.property-offer-modal {
    .field-wrap__input--radio input + label .check-element {
        background: theme-color('mid-grey');
    }
}

.package-color-red {
    color: theme-color('primary');
}

.package-color-blue {
    color: theme-color('let');
}

.package-color-yellow {
    color: theme-color('rent');
}

.btn--feature {
    width: 3.5rem;
    height: 3.5rem;
}

.property-actions {
    position: sticky;
    bottom: 2rem;
    left: 0;
    z-index: 1;
}

.file-download {
    height: 35rem;
    padding: 1rem;
    background-color: theme-color('light-grey');
    color: theme-color('mid-grey');

    .svg-sprite {
        width: 100%;
        max-width: 15rem;
    }
}
</style>
